import {IconButton} from '@dropbox/dig-components/buttons';
import {Chip} from '@dropbox/dig-components/chip';
import {LabelGroup} from '@dropbox/dig-components/label_group';
import {Menu} from '@dropbox/dig-components/menu';
import {TextArea} from '@dropbox/dig-components/text_fields';
import {Text} from '@dropbox/dig-components/typography';
import {atoms, Box, Stack, withShade} from '@dropbox/dig-foundations';
import {UIIcon} from '@dropbox/dig-icons';
import {
  AddLine,
  CheckmarkLine,
  MoreHorizontalLine,
  PaperDocLine,
  SendLine,
  WorkstreamsLine,
} from '@dropbox/dig-icons/assets';
import {pulseUserAtom} from 'atoms/auth';
import {snackbarAtom} from 'atoms/snackbar';
import cx from 'classnames';
import {
  EditorWorkstreamAssociation,
  EditorWorkstreamType,
  Employee,
  EmployeeRoleLevel,
  WorkstreamAccess,
  WorkstreamActionType,
  WorkstreamBasic,
  WorkstreamDependencyType,
  WorkstreamEdit,
  WorkstreamMetric,
  WorkstreamMetricVisibility,
  WorkstreamStatus,
} from 'client';
import {useCollabId, usePlanningId} from 'components/DSYS/CollabProvider';
import {DeleteModal} from 'components/DSYS/DeleteModal';
import {resolveStampedEditorStateMapValue} from 'components/DSYS/editor/dynamicNode/overlayHelpers';
import {PlanningCommentableRichText} from 'components/DSYS/editor/dynamicNode/PlanningCommentableRichText';
import {planningMapKeys} from 'components/DSYS/editor/dynamicNode/planningCommentMapKeys';
import {PlanningStructuredCommentField} from 'components/DSYS/editor/dynamicNode/PlanningStructuredCommentField';
import {structuredCommentSegment} from 'components/DSYS/editor/dynamicNode/structuredCommentState';
import {TOGGLE_SELECTED_TEXT_TOOLBAR_COMMAND} from 'components/DSYS/editor/plugins/SelectedTextToolbar';
import type {EditorTextSize} from 'components/DSYS/editor/themes/EditorTheme';
import {
  getWorkstreamActionIcon,
  getWorkstreamActionText,
  WorkstreamNodeType,
} from 'components/DSYS/editor/utils';
import {
  isLexicalJsonString,
  parseAndExtractTextFromLexicalJson,
  toPlainTextLexicalJson,
} from 'components/DSYS/editor/utils/lexical';
import {OptionalTooltip} from 'components/DSYS/OptionalTooltip';
import {RichTextArea} from 'components/DSYS/RichTextArea';
import {isClosedStatus} from 'components/DSYS/StatusButtonIcon';
import {ROUTE_PATHS} from 'constant';
import {isUserAdmin} from 'helpers/utils';
import {useOutsideClick} from 'hooks/useOutsideClick';
import {t} from 'i18next';
import {useAtomValue, useSetAtom} from 'jotai';
import {$getNodeByKey, LexicalEditor} from 'lexical';
import {Suspense, useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {
  useEditorWorkstreams,
  useIsPlanningDocPublished,
  usePlanning,
  useWorkstreamsUpdate,
} from 'views/planning/hooks';
import {queryClient} from 'views/QueryClientWrapper';
import {toWorkstreamEditPayload} from 'views/workstreams/detail/workstreamEditPayload';
import type {
  MetricsForecastType,
  RedesignMetricTypeaheadSummaryMode,
} from 'views/workstreams/detail/WorkstreamRedesignSections';
import {
  buildMetricsPeriodRange,
  compareMetricsPeriods,
  getCurrentMetricsPeriod,
  getMetricsPeriodWithOffset,
  normalizeMetricsPeriodOptions,
  REDESIGN_OUTCOME_FILTERS,
  RedesignMetricsList,
  RedesignMetricsPeriodSelector,
  RedesignOutcomesList,
} from 'views/workstreams/detail/WorkstreamRedesignSections';
import {
  useCreateSwitchboard,
  useDescriptionEditor,
  useWorkstream,
  useWorkstreamEdit,
  useWorkstreamMetricMutation,
  useWorkstreamUndelete,
} from 'views/workstreams/hooks';
import {getSubworkstreamSelectionErrorKey} from 'views/workstreams/subworkstreamValidation';
import {getSwitchboardActionState} from 'views/workstreams/switchboardActions';
import {
  DatePickerEditor,
  EmployeeChip,
  EmployeeSelect,
  MultiEmployeeSelect,
} from 'views/workstreams/TODODSYSStuff';
import {
  getFullWorkstreamMetrics,
  getPrimaryMetricForWorkstreamType,
  getWorkstreamDri,
  getWorkstreamExecSponsor,
  getWorkstreamWorkingGroup,
  isFullWorkstreamMetric,
  isPrimaryMetric,
  isWorkstreamStatusArchived,
  isWorkstreamStatusClosed,
} from 'views/workstreams/utils';
import {WorkstreamOutcomeModify} from 'views/workstreams/WorkstreamOutcomeModify';
import {
  WorkstreamTypeahead,
  WorkstreamTypeaheadComponent,
} from 'views/workstreams/WorkstreamTypeahead';

import {
  $isTypeaheadNode,
  getTypeaheadNodeTableContext,
  hideDeletedAndPrivateWorkstreamsInWorkstreamTable,
  syncWorkstreamTableRows,
} from './TypeaheadNode';
import {useSyncWorkstreamNameWithEditorState, useTableCellWidth} from './utils';
import styles from './WorkstreamEditComponent.module.css';
import {resolveSwitchboardNodeWorkstreamId} from './workstreamNodeScope';

type HandleContentEditorStateChange = (
  entityId: number,
  contentEditorState: string | undefined,
  sourceText?: string
) => void;

/** Strategy kernel passes scoped collab keys via {@link StrategyKernelScopedPlanningCollabExtras.persistScopedFullKey}. */
export type StrategyKernelScopedPlanningCollabExtras = {
  scopedField: 'leadMetricEditorStateMap' | 'lagMetricEditorStateMap' | 'milestonesEditorStateMap';
  persistScopedFullKey: (
    fullMapKey: string,
    serializedState: string | undefined,
    sourceText: string | undefined
  ) => void;
};

function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number,
  immediate: boolean = false
): (...args: Parameters<T>) => void {
  let timeout: ReturnType<typeof setTimeout> | null;

  return function (this: ThisParameterType<T>, ...args: Parameters<T>): void {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const context = this;
    const later = function () {
      timeout = null;
      if (!immediate) func.apply(context, args);
    };

    const callNow = immediate && !timeout;
    if (timeout !== null) {
      clearTimeout(timeout);
    }
    timeout = setTimeout(later, wait);

    if (callNow) func.apply(context, args);
  };
}

export const WorkstreamEditComponentWrapper = ({
  editor,
  nodeType,
  workstreamIndex,
  onRemoveWorkstream,
  initialContentEditorState,
  handleContentEditorStateChange,
  nodeKey,
}: {
  editor: LexicalEditor;
  nodeType: WorkstreamNodeType;
  workstreamIndex?: number;
  onRemoveWorkstream: (workstreamId: number) => Promise<void>;
  initialContentEditorState: string | undefined;
  handleContentEditorStateChange: HandleContentEditorStateChange;
  nodeKey?: string;
}) => {
  const planningId = usePlanningId();
  const {planning} = usePlanning(planningId ?? '');
  const {narrativeWorkstreams: workstreams} = useEditorWorkstreams(planningId ?? '');

  // Get the workstream ID
  const workstreamId = workstreams?.[workstreamIndex ?? -1]?.workstream_id;
  const switchboardId = planning?.switchboard_id ?? undefined;

  // Fetch the workstream data
  const {workstream: fullWorkstream} = useWorkstream(workstreamId);
  const {workstream: switchboardWorkstream} = useWorkstream(switchboardId);

  // Store the full workstream data in node's __value for export
  const workstreamToSync = switchboardWorkstream ?? fullWorkstream;
  useEffect(() => {
    if (workstreamToSync && nodeKey) {
      editor.update(() => {
        const node = $getNodeByKey(nodeKey);
        if (node && $isTypeaheadNode(node)) {
          const writableNode = node.getWritable() as any;
          writableNode.__value = workstreamToSync;
        }
      });
    }
  }, [workstreamToSync, nodeKey, editor]);

  if (!planningId || !planning) {
    return null;
  }

  if (switchboardId) {
    return (
      <SwitchboardDocEditComponent
        editor={editor}
        nodeType={nodeType}
        switchboardId={switchboardId}
        nodeKey={nodeKey}
        initialContentEditorState={initialContentEditorState}
        handleContentEditorStateChange={handleContentEditorStateChange}
      />
    );
  }

  return (
    <NarrativeDocEditComponent
      editor={editor}
      nodeType={nodeType}
      workstreamIndex={workstreamIndex}
      onRemoveWorkstream={onRemoveWorkstream}
      initialContentEditorState={initialContentEditorState}
      handleContentEditorStateChange={handleContentEditorStateChange}
    />
  );
};

// Typeahead nodes for switchboard docs
const SwitchboardDocEditComponent = ({
  editor,
  nodeType,
  switchboardId,
  nodeKey,
  initialContentEditorState,
  handleContentEditorStateChange,
}: {
  editor: LexicalEditor;
  nodeType: WorkstreamNodeType;
  switchboardId: number;
  nodeKey?: string;
  initialContentEditorState: string | undefined;
  handleContentEditorStateChange: HandleContentEditorStateChange;
}) => {
  const planningId = usePlanningId();

  const {workstream: switchboardWorkstream} = useWorkstream(switchboardId);
  const switchboardSubWorkstreams = switchboardWorkstream?.dependencies.map(
    (dependency) => dependency.dependent
  );
  const switchboardSubWorkstreamsInfo = useMemo(() => {
    return (
      switchboardSubWorkstreams?.map((workstream) => {
        return {
          id: workstream.id,
          name: workstream.name,
          archived: workstream.archived ?? false,
          privacy: workstream.privacy,
        };
      }) ?? []
    );
  }, [switchboardSubWorkstreams]);

  useEffect(() => {
    if (switchboardSubWorkstreamsInfo.length > 0) {
      syncWorkstreamTableRows(
        editor,
        planningId,
        switchboardSubWorkstreamsInfo.length,
        'workstream-name'
      );
      hideDeletedAndPrivateWorkstreamsInWorkstreamTable(
        editor,
        switchboardSubWorkstreamsInfo,
        'workstream-name'
      );
    }
  }, [editor, switchboardSubWorkstreamsInfo, planningId]);

  // Typeahead nodes for main switchboard workstream
  if (nodeType === 'switchboard-subworkstreams' || nodeType === 'workstream-subworkstreams') {
    return (
      <SubworkstreamSelectionWrapper
        editor={editor}
        workstreamId={switchboardId}
        initialContentEditorState={initialContentEditorState}
        handleContentEditorStateChange={handleContentEditorStateChange}
      />
    );
  } else if (nodeType === 'switchboard-link') {
    return <WorkstreamLink workstreamId={switchboardId} />;
  } else if (nodeType === 'switchboard-objective') {
    return (
      <WorkstreamObjectiveWrapper
        workstreamId={switchboardId}
        initialContentEditorState={initialContentEditorState}
        handleContentEditorStateChange={handleContentEditorStateChange}
      />
    );
  } else if (nodeType === 'switchboard-objective-editable') {
    return (
      <WorkstreamObjectiveEditableWrapper
        workstreamId={switchboardId}
        initialContentEditorState={initialContentEditorState}
        handleContentEditorStateChange={handleContentEditorStateChange}
      />
    );
  } else if (nodeType === 'switchboard-eta') {
    return <WorkstreamEtaWrapper editor={editor} workstreamId={switchboardId} />;
  }

  const switchboardSubWorkstreamIds = switchboardSubWorkstreamsInfo.map(
    (workstream) => workstream.id
  );
  const tableContext = nodeKey ? getTypeaheadNodeTableContext(editor, nodeKey) : null;
  const isRepeatedSubworkstreamTable =
    !!tableContext &&
    tableContext.bodyRowCount > 0 &&
    tableContext.workstreamNameNodeCount === tableContext.bodyRowCount;
  const repeatedTableRowIndex =
    isRepeatedSubworkstreamTable &&
    tableContext &&
    tableContext.rowIndex !== null &&
    tableContext.rowIndex !== -1
      ? tableContext.rowIndex
      : undefined;
  const switchboardWorkstreamId = resolveSwitchboardNodeWorkstreamId({
    nodeType,
    switchboardId,
    switchboardSubWorkstreamIds,
    workstreamIndex: repeatedTableRowIndex,
  });
  const switchboardSubWorkstreamId =
    repeatedTableRowIndex !== undefined
      ? switchboardSubWorkstreamIds[repeatedTableRowIndex]
      : undefined;

  switch (nodeType) {
    case 'switchboard-name':
      return (
        <WorkstreamNameWrapper
          editor={editor}
          workstreamId={switchboardId}
          initialContentEditorState={initialContentEditorState}
          handleContentEditorStateChange={handleContentEditorStateChange}
          hideButtons
          hideDescription
        />
      );
    case 'workstream-name':
      if (!switchboardSubWorkstreamId || !switchboardWorkstreamId) {
        return (
          <WorkstreamNameWrapper
            editor={editor}
            workstreamId={switchboardId}
            initialContentEditorState={initialContentEditorState}
            handleContentEditorStateChange={handleContentEditorStateChange}
            hideActionMenu
          />
        );
      }
      return (
        <WorkstreamNameWrapper
          editor={editor}
          workstreamId={switchboardWorkstreamId ?? switchboardSubWorkstreamId ?? switchboardId}
          initialContentEditorState={initialContentEditorState}
          handleContentEditorStateChange={handleContentEditorStateChange}
          hideActionMenu
        />
      );
    case 'workstream-outcomes':
      if (!switchboardWorkstreamId) {
        return null;
      }
      return (
        <WorkstreamOutcomesWrapper
          workstreamId={switchboardWorkstreamId}
          initialContentEditorState={initialContentEditorState}
          handleContentEditorStateChange={handleContentEditorStateChange}
          isLongterm={false}
        />
      );
    case 'workstream-definition-of-done':
      if (!switchboardWorkstreamId) {
        return null;
      }
      return (
        <WorkstreamOutcomesWrapper
          workstreamId={switchboardWorkstreamId}
          initialContentEditorState={initialContentEditorState}
          handleContentEditorStateChange={handleContentEditorStateChange}
          isLongterm={true}
        />
      );
    case 'workstream-metrics':
      if (!switchboardWorkstreamId) {
        return null;
      }
      return (
        <WorkstreamMetricsWrapper
          workstreamId={switchboardWorkstreamId}
          initialContentEditorState={initialContentEditorState}
          handleContentEditorStateChange={handleContentEditorStateChange}
        />
      );
    case 'workstream-primary-lead-metric':
      return (
        <PrimaryWorkstreamMetricWrapper
          workstreamId={switchboardWorkstreamId ?? switchboardId}
          metricType="leading"
          initialContentEditorState={initialContentEditorState}
          handleContentEditorStateChange={handleContentEditorStateChange}
        />
      );
    case 'workstream-primary-lag-metric':
      return (
        <PrimaryWorkstreamMetricWrapper
          workstreamId={switchboardWorkstreamId ?? switchboardId}
          metricType="lagging"
          initialContentEditorState={initialContentEditorState}
          handleContentEditorStateChange={handleContentEditorStateChange}
        />
      );
    case 'workstream-starred-milestones':
      return (
        <StarredMilestonesWrapper
          workstreamId={switchboardWorkstreamId ?? switchboardId}
          initialContentEditorState={initialContentEditorState}
          handleContentEditorStateChange={handleContentEditorStateChange}
        />
      );
    case 'workstream-objective':
      return (
        <WorkstreamObjectiveWrapper
          workstreamId={switchboardWorkstreamId ?? switchboardId}
          initialContentEditorState={initialContentEditorState}
          handleContentEditorStateChange={handleContentEditorStateChange}
        />
      );
    case 'workstream-objective-editable':
      return (
        <WorkstreamObjectiveEditableWrapper
          workstreamId={switchboardWorkstreamId ?? switchboardId}
          initialContentEditorState={initialContentEditorState}
          handleContentEditorStateChange={handleContentEditorStateChange}
        />
      );
    case 'switchboard-dri':
      return <WorkstreamDriWrapper editor={editor} workstreamId={switchboardId} />;
    case 'workstream-dri':
      return (
        <WorkstreamDriWrapper
          editor={editor}
          workstreamId={switchboardWorkstreamId ?? switchboardId}
        />
      );
    case 'switchboard-exec-sponsor':
      return <WorkstreamExecSponsorWrapper editor={editor} workstreamId={switchboardId} />;
    case 'workstream-exec-sponsor':
      return (
        <WorkstreamExecSponsorWrapper
          editor={editor}
          workstreamId={switchboardWorkstreamId ?? switchboardId}
        />
      );
    case 'switchboard-working-group':
      return <WorkstreamWorkingGroupWrapper editor={editor} workstreamId={switchboardId} />;
    case 'workstream-eta':
      return (
        <WorkstreamEtaWrapper
          editor={editor}
          workstreamId={switchboardWorkstreamId ?? switchboardId}
        />
      );
    case 'workstream-link':
      return <WorkstreamLink workstreamId={switchboardWorkstreamId ?? switchboardId} />;
  }
};

// Typeahead nodes for narrative docs
const NarrativeDocEditComponent = ({
  editor,
  nodeType,
  workstreamIndex,
  onRemoveWorkstream,
  initialContentEditorState,
  handleContentEditorStateChange,
}: {
  editor: LexicalEditor;
  nodeType: WorkstreamNodeType;
  workstreamIndex?: number;
  onRemoveWorkstream: (workstreamId: number) => Promise<void>;
  initialContentEditorState: string | undefined;
  handleContentEditorStateChange: HandleContentEditorStateChange;
}) => {
  const planningId = usePlanningId();
  const {narrativeWorkstreams: workstreams, isLoading} = useEditorWorkstreams(planningId ?? '');
  if (!workstreams || workstreamIndex === undefined || isLoading) {
    return null;
  }
  const workstream = workstreams[workstreamIndex];
  if (!workstream) {
    return null;
  }

  const workstreamId = workstream.workstream_id;
  const contentEditorStateObj = JSON.parse(initialContentEditorState || '{}');
  const workstreamContentEditorState = contentEditorStateObj[workstreamId];
  switch (nodeType) {
    case 'workstream-name':
      return (
        <WorkstreamNameWrapper
          editor={editor}
          workstreamId={workstreamId}
          onRemoveWorkstream={onRemoveWorkstream}
          initialContentEditorState={workstreamContentEditorState}
          handleContentEditorStateChange={handleContentEditorStateChange}
          hideActionMenu
        />
      );
    case 'workstream-objective':
      return (
        <WorkstreamObjectiveWrapper
          workstreamId={workstreamId}
          initialContentEditorState={workstreamContentEditorState}
          handleContentEditorStateChange={handleContentEditorStateChange}
        />
      );
    case 'workstream-objective-editable':
      return (
        <WorkstreamObjectiveEditableWrapper
          workstreamId={workstreamId}
          initialContentEditorState={workstreamContentEditorState}
          handleContentEditorStateChange={handleContentEditorStateChange}
        />
      );
    case 'workstream-outcomes':
      return (
        <WorkstreamOutcomesWrapper
          workstreamId={workstreamId}
          initialContentEditorState={initialContentEditorState}
          handleContentEditorStateChange={handleContentEditorStateChange}
          isLongterm={false}
        />
      );
    case 'workstream-metrics':
      return (
        <WorkstreamMetricsWrapper
          workstreamId={workstreamId}
          initialContentEditorState={initialContentEditorState}
          handleContentEditorStateChange={handleContentEditorStateChange}
        />
      );
    case 'workstream-primary-lead-metric':
      return (
        <PrimaryWorkstreamMetricWrapper
          workstreamId={workstreamId}
          metricType="leading"
          initialContentEditorState={initialContentEditorState}
          handleContentEditorStateChange={handleContentEditorStateChange}
        />
      );
    case 'workstream-primary-lag-metric':
      return (
        <PrimaryWorkstreamMetricWrapper
          workstreamId={workstreamId}
          metricType="lagging"
          initialContentEditorState={initialContentEditorState}
          handleContentEditorStateChange={handleContentEditorStateChange}
        />
      );
    case 'workstream-starred-milestones':
      return (
        <StarredMilestonesWrapper
          workstreamId={workstreamId}
          initialContentEditorState={initialContentEditorState}
          handleContentEditorStateChange={handleContentEditorStateChange}
        />
      );
    case 'workstream-definition-of-done':
      return (
        <WorkstreamOutcomesWrapper
          workstreamId={workstreamId}
          initialContentEditorState={initialContentEditorState}
          handleContentEditorStateChange={handleContentEditorStateChange}
          isLongterm={true}
        />
      );
    case 'workstream-dri':
      return <WorkstreamDriWrapper editor={editor} workstreamId={workstreamId} />;
    case 'workstream-exec-sponsor':
      return <WorkstreamExecSponsorWrapper editor={editor} workstreamId={workstreamId} />;
    case 'workstream-subworkstreams':
      return (
        <SubworkstreamSelectionWrapper
          editor={editor}
          workstreamId={workstreamId}
          initialContentEditorState={initialContentEditorState}
          handleContentEditorStateChange={handleContentEditorStateChange}
        />
      );
    case 'workstream-action':
      return <WorkstreamActionMenuWrapper workstreamId={workstreamId} />;
    default:
      return null;
  }
};

export const ContributingWorkstreamEditComponent = ({
  editor,
  nodeType,
  workstreamIndex,
  onAddWorkstream,
  onRemoveWorkstream,
  initialContentEditorState,
  handleContentEditorStateChange,
  nodeKey,
}: {
  editor: LexicalEditor;
  nodeType: WorkstreamNodeType;
  workstreamIndex: number;
  onAddWorkstream: (workstream: WorkstreamBasic) => Promise<void>;
  onRemoveWorkstream: (workstreamId: number) => Promise<void>;
  initialContentEditorState: string | undefined;
  handleContentEditorStateChange: HandleContentEditorStateChange;
  nodeKey: string;
}) => {
  const planningId = usePlanningId();
  const {contributingWorkstreams, isLoading} = useEditorWorkstreams(planningId ?? '');
  const contributingWorkstream = contributingWorkstreams[workstreamIndex];
  const {workstream: fullContributingWorkstream} = useWorkstream(
    contributingWorkstream?.workstream_id
  );
  const workstreamId = contributingWorkstream?.workstream_id ?? undefined;

  // Store the contributing workstream data in node's __value for export
  useEffect(() => {
    if (nodeKey) {
      editor.update(() => {
        const node = $getNodeByKey(nodeKey);
        if (node && $isTypeaheadNode(node)) {
          const writableNode = node.getWritable() as any;
          const subWorkstreamsMap = new Map<number, WorkstreamBasic>();
          fullContributingWorkstream?.dependencies?.forEach((dependency) => {
            subWorkstreamsMap.set(dependency.dependent.id, dependency.dependent);
          });
          const selectedSubWorkstreams =
            contributingWorkstream?.contributing_workstream_ids?.flatMap((id) => {
              const ws = subWorkstreamsMap.get(id);
              return ws ? [ws] : [];
            }) ?? [];
          // Override workstream with selected subworkstreams for export
          const workstreamToSync = fullContributingWorkstream
            ? {
                ...fullContributingWorkstream,
                dependencies: selectedSubWorkstreams,
              }
            : undefined;
          writableNode.__value = workstreamToSync;
        }
      });
    }
  }, [fullContributingWorkstream, contributingWorkstream, nodeKey, editor]);

  if (isLoading) {
    return null;
  }
  if (!workstreamId) {
    return nodeType === 'contributing-workstream-name' ? (
      <AddWorkstreamComponent
        editor={editor}
        onSelect={onAddWorkstream}
        archiveWorkstreams={false}
      />
    ) : null;
  }

  const contentEditorStateObj = JSON.parse(initialContentEditorState || '{}');
  const workstreamContentEditorState = contentEditorStateObj[workstreamId];
  switch (nodeType) {
    case 'contributing-workstream-name':
      return (
        <WorkstreamTypeaheadSelectionWrapper
          editor={editor}
          workstreamId={workstreamId}
          onDelete={onRemoveWorkstream}
          editorStateMapJson={initialContentEditorState}
          initialContentEditorState={workstreamContentEditorState}
          handleContentEditorStateChange={handleContentEditorStateChange}
        />
      );
    case 'contributing-workstream-subworkstreams':
      return (
        <ContributingWorkstreamSubworkstreams
          editor={editor}
          contributingWorkstream={contributingWorkstream}
          initialContentEditorState={initialContentEditorState}
          handleContentEditorStateChange={handleContentEditorStateChange}
        />
      );
    default:
      return null;
  }
};

const ContributingWorkstreamSubworkstreams = ({
  editor,
  contributingWorkstream,
  initialContentEditorState,
  handleContentEditorStateChange,
}: {
  editor: LexicalEditor;
  contributingWorkstream: EditorWorkstreamAssociation;
  initialContentEditorState: string | undefined;
  handleContentEditorStateChange: HandleContentEditorStateChange;
}) => {
  const planningId = usePlanningId();
  const isPlanningDocPublished = useIsPlanningDocPublished(planningId ?? '');
  const workstreamId = contributingWorkstream.workstream_id;
  const {workstream, isLoading} = useWorkstream(workstreamId);
  const {mutateAsync: updateWorkstreams} = useWorkstreamsUpdate(planningId ?? '');

  const [isFetching, setIsFetching] = useState(false);
  const typeaheadRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);

  useOutsideClick(typeaheadRef, () => setIsOpen(false));

  const handleOpen = () => {
    setIsOpen(true);
  };

  const handleSelectSubworkstream = async (selection: WorkstreamBasic) => {
    await updateWorkstreams({
      workstreams: [workstreamId],
      action: 'add_contributing_id',
      workstreamType: EditorWorkstreamType.CONTRIBUTING_NARRATIVE,
      contributingWorkstreamId: selection.id,
    });
  };

  const handleDeleteSubworkstream = async (subWorkstreamId: number) => {
    await updateWorkstreams({
      workstreams: [workstreamId],
      action: 'remove_contributing_id',
      workstreamType: EditorWorkstreamType.CONTRIBUTING_NARRATIVE,
      contributingWorkstreamId: subWorkstreamId,
    });
  };

  if (isLoading) {
    return null;
  }

  if (!workstream?.dependencies?.length) {
    return (
      <Text size="small" color="subtle">
        {t('workstream_no_subworkstreams')}
      </Text>
    );
  }

  const selectedSubWorkstreamIds = contributingWorkstream.contributing_workstream_ids ?? [];
  const showNoText = selectedSubWorkstreamIds.length;
  const subWorkstreamsMap = new Map<number, WorkstreamBasic>();
  workstream.dependencies.forEach((dependency) => {
    subWorkstreamsMap.set(dependency.dependent.id, dependency.dependent);
  });
  const allSubWorkstreams = Array.from(subWorkstreamsMap.values());
  const selectedSubWorkstreams = selectedSubWorkstreamIds.flatMap((id) => {
    const ws = subWorkstreamsMap.get(id);
    return ws ? [ws] : [];
  });
  const unselectedSubWorkstreams: WorkstreamBasic[] = allSubWorkstreams.filter(
    (ws) => !selectedSubWorkstreamIds.includes(ws.id)
  );
  const contentEditorStateObj = JSON.parse(initialContentEditorState || '{}');

  const selectedSubWorkstreamsComponent = selectedSubWorkstreams.map((subWorkstream) => (
    <WorkstreamTypeaheadSelection
      key={`editor-contributing-subworkstream__${subWorkstream.id}`}
      editor={editor}
      basicWorkstream={subWorkstream}
      onDelete={handleDeleteSubworkstream}
      initialContentEditorState={contentEditorStateObj[subWorkstream.id]}
      handleContentEditorStateChange={handleContentEditorStateChange}
      disableDraftWorkstreams
    />
  ));

  if (isOpen) {
    return (
      <>
        {selectedSubWorkstreamsComponent}
        <Box ref={typeaheadRef} marginTop={showNoText ? '4' : '0'}>
          <WorkstreamTypeaheadComponent
            workstreamOptions={unselectedSubWorkstreams}
            isLoading={false}
            placeholder={t('select_subworkstream')}
            selectedIds={[]}
            menuStyle={{minWidth: 365}}
            onSelect={async (subWorkstream: WorkstreamBasic) => {
              editor.dispatchCommand(TOGGLE_SELECTED_TEXT_TOOLBAR_COMMAND, {open: false});
              setIsFetching(true);
              await handleSelectSubworkstream(subWorkstream);
              setIsOpen(false);
              setIsFetching(false);
              editor.dispatchCommand(TOGGLE_SELECTED_TEXT_TOOLBAR_COMMAND, {open: true});
            }}
            inputIcon={
              <UIIcon
                src={WorkstreamsLine}
                className={atoms({color: 'Text Base', marginTop: '8'})}
              />
            }
            disableCreateWorkstream
            autoFocus
          />
        </Box>
      </>
    );
  }
  if (showNoText) {
    return (
      <>
        {selectedSubWorkstreamsComponent}
        {!isPlanningDocPublished && unselectedSubWorkstreams.length > 0 && (
          <Box marginTop="4" style={{height: '40px'}}>
            <IconButton
              variant={isFetching ? 'opacity' : 'outline'}
              size="small"
              shape="circular"
              onClick={handleOpen}
              disabled={isFetching}
              aria-label="icon button"
            >
              <UIIcon src={AddLine} size="small" />
            </IconButton>
          </Box>
        )}
      </>
    );
  }
  return isPlanningDocPublished ? null : (
    <WorkstreamButtonComponent iconSrc={AddLine} text={t('add_workstream')} onClick={handleOpen} />
  );
};

export const WorkstreamMetricEditComponent = ({
  editor,
  nodeType,
  metricIndex,
  initialContentEditorState,
  handleContentEditorStateChange,
  // nodeKey,
}: {
  editor: LexicalEditor;
  nodeType: WorkstreamNodeType;
  metricIndex: number;
  initialContentEditorState: string | undefined;
  handleContentEditorStateChange: HandleContentEditorStateChange;
  nodeKey: string;
}) => {
  const planningId = usePlanningId();
  const {planning} = usePlanning(planningId ?? '');

  const switchboardId = planning?.switchboard_id ?? undefined;
  const {isLoading, workstream: switchboardWorkstream} = useWorkstream(switchboardId);
  const {createMetric, updateMetric, isPending: isLoadingMetric} = useWorkstreamMetricMutation();

  const metrics = (switchboardWorkstream?.metrics ?? [])
    .filter(isFullWorkstreamMetric)
    .filter((m) => m.type === 'metric')
    .sort((a, b) => {
      if (a.complete_date && b.complete_date) {
        const dateCompare = a.complete_date.localeCompare(b.complete_date);
        if (dateCompare !== 0) return dateCompare;
        return a.id - b.id;
      }

      if (a.complete_date && !b.complete_date) {
        return -1;
      }

      if (!a.complete_date && b.complete_date) {
        return 1;
      }

      return a.id - b.id;
    });
  const metric = metrics[metricIndex];

  const metricsLength = metrics.length;
  useEffect(() => {
    if (metricsLength > 0) {
      // Add 1 extra row for 'Add' metric button
      syncWorkstreamTableRows(editor, planningId, metricsLength + 1, 'workstream-metric');
    }
  }, [editor, metricsLength, planningId]);

  const handleMetricChange = async (
    metricid: number,
    metricUpdate: Partial<WorkstreamMetric>,
    reason: string
  ) => {
    if (!switchboardId) return;
    if (metricid === -1) {
      await createMetric(switchboardId, metricUpdate, metricsLength + 1, reason, true);
    } else {
      await updateMetric(switchboardId, metricid, metric, metricUpdate, reason, true);
    }
  };

  if (isLoading || !switchboardId) {
    return null;
  }

  if (!metric) {
    return nodeType === 'workstream-metric' ? (
      <WorkstreamOutcomeModify
        workstreamId={switchboardId}
        metric={{
          id: -1,
          title: '',
          type: 'metric',
          visibility: WorkstreamMetricVisibility.FULL,
          is_longterm: true,
          current: null,
          target: null,
          complete: false,
          complete_date: null,
          url: null,
          is_mnpi: false,
          owner: null,
          created_at: '',
          updated_at: '',
        }}
        isCreate
        isPending={isLoadingMetric}
        onSave={(update) => handleMetricChange(-1, update, 'Add new metric')}
        disableMetricComplete
        disableMetricTypeChange
      />
    ) : null;
  }

  const contentEditorStateObj = JSON.parse(initialContentEditorState || '{}');
  const metricContentEditorState = contentEditorStateObj[metric.id];
  switch (nodeType) {
    case 'workstream-metric':
      return (
        <WorkstreamMetricTextWithEditButton
          key={`editor-workstream-metric-title__${metric.id}`}
          editor={editor}
          workstreamId={switchboardId}
          metric={metric}
          metricType="title"
          metricValue={metric.title}
          handleMetricChange={handleMetricChange}
          editorStateMapJson={initialContentEditorState}
          metricContentEditorState={metricContentEditorState}
          handleContentEditorStateChange={handleContentEditorStateChange}
          showEditButton
        />
      );
    case 'workstream-metric-dri':
      return (
        <WorkstreamMetricDri
          key={`editor-workstream-metric-dri__${metric.id}`}
          editor={editor}
          metric={metric}
          handleMetricChange={handleMetricChange}
          isLoadingMetric={isLoadingMetric}
        />
      );
    case 'workstream-metric-current-value':
      return (
        <WorkstreamMetricTextWithEditButton
          key={`editor-workstream-metric-current-value__${metric.id}`}
          editor={editor}
          workstreamId={switchboardId}
          metric={metric}
          metricType="current"
          metricValue={metric.current ?? undefined}
          handleMetricChange={handleMetricChange}
          editorStateMapJson={initialContentEditorState}
          metricContentEditorState={metricContentEditorState}
          handleContentEditorStateChange={handleContentEditorStateChange}
        />
      );
    case 'workstream-metric-target-value':
      return (
        <WorkstreamMetricTextWithEditButton
          key={`editor-workstream-metric-target-value__${metric.id}`}
          editor={editor}
          workstreamId={switchboardId}
          metric={metric}
          metricType="target"
          metricValue={metric.target ?? undefined}
          handleMetricChange={handleMetricChange}
          editorStateMapJson={initialContentEditorState}
          metricContentEditorState={metricContentEditorState}
          handleContentEditorStateChange={handleContentEditorStateChange}
        />
      );
    case 'workstream-metric-eta':
      return (
        <WorkstreamMetricEta
          key={`editor-workstream-metric-eta__${metric.id}`}
          editor={editor}
          metric={metric}
          handleMetricChange={handleMetricChange}
          isLoadingMetric={isLoadingMetric}
        />
      );
    default:
      return null;
  }
};

const WorkstreamMetricTextWithEditButton = ({
  workstreamId,
  metric,
  metricType,
  metricValue,
  handleMetricChange,
  editorStateMapJson,
  metricContentEditorState,
  handleContentEditorStateChange,
  showEditButton = false,
}: {
  editor: LexicalEditor;
  workstreamId: number;
  metric: WorkstreamMetric;
  metricType: 'title' | 'current' | 'target';
  metricValue: string | undefined;
  handleMetricChange: (
    metricid: number,
    metricUpdate: Partial<WorkstreamMetric>,
    reason: string
  ) => Promise<void>;
  editorStateMapJson?: string;
  metricContentEditorState: string | undefined;
  handleContentEditorStateChange: HandleContentEditorStateChange;
  showEditButton?: boolean;
}) => {
  const planningId = usePlanningId();

  const {deleteMetric} = useWorkstreamMetricMutation();

  const textRef = useRef<HTMLDivElement>(null);

  const [isHovering, setIsHovering] = useState(false);
  const [isPending, setIsPending] = useState(false);

  useOutsideClick(textRef, () => setIsHovering(false));

  const handleSave = useCallback(
    async (update: Partial<WorkstreamMetric>) => {
      setIsPending(true);
      await handleMetricChange(metric.id, update, `Update metric ${metricType}`);
      setIsPending(false);
    },
    [handleMetricChange, metric.id, metricType]
  );

  const handleDeleteMetric = useCallback(async () => {
    await deleteMetric({workstreamId, metricId: metric.id}, 'Delete metric', true);
  }, [workstreamId, metric.id, deleteMetric]);

  const fallbackText = String(metricValue ?? '');
  const displayValue = editorStateMapJson
    ? resolveStampedEditorStateMapValue(editorStateMapJson, String(metric.id), fallbackText)
    : (metricContentEditorState ?? metricValue ?? '');

  return (
    <Box
      ref={textRef}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
    >
      <Box
        display="flex"
        alignItems="flex-start"
        justifyContent="space-between"
        style={{gap: '8px'}}
      >
        <Box>
          <PlanningCommentableRichText
            planningId={planningId}
            value={displayValue}
            onChange={(state) => {
              handleContentEditorStateChange?.(metric.id, state.toString(), fallbackText);
            }}
          />
        </Box>
        {showEditButton && (
          <Box style={{visibility: isHovering ? 'visible' : 'hidden', marginTop: '-4px'}}>
            <WorkstreamOutcomeModify
              workstreamId={workstreamId}
              isCreate={false}
              metric={metric}
              isPending={isPending}
              onSave={handleSave}
              onDelete={handleDeleteMetric}
              disableMetricComplete
              disableMetricTypeChange
              disableChangeReason
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};

const WorkstreamMetricDri = ({
  editor,
  metric,
  handleMetricChange,
  isLoadingMetric,
}: {
  editor: LexicalEditor;
  metric: WorkstreamMetric;
  handleMetricChange: (
    metricid: number,
    metricUpdate: Partial<WorkstreamMetric>,
    reason: string
  ) => Promise<void>;
  isLoadingMetric: boolean;
}) => {
  const planningId = usePlanningId();
  const isPlanningDocPublished = useIsPlanningDocPublished(planningId ?? '');

  const [employee, setEmployee] = useState<EmployeeRoleLevel | undefined>(
    metric.owner ?? undefined
  );

  // Update employee when metric owner changes
  useEffect(() => {
    setEmployee(metric.owner ?? undefined);
  }, [metric.owner]);

  const ref = useRef<HTMLDivElement>(null);
  const {cellWidth} = useTableCellWidth({ref, trigger: metric.id});

  const handleUpdate = async (newEmployee?: Employee) => {
    if (!newEmployee || newEmployee.ldap === employee?.ldap) return;
    editor.dispatchCommand(TOGGLE_SELECTED_TEXT_TOOLBAR_COMMAND, {open: false});
    await handleMetricChange(metric.id, {owner: newEmployee}, 'Update metric DRI');
    setEmployee(newEmployee);
    editor.dispatchCommand(TOGGLE_SELECTED_TEXT_TOOLBAR_COMMAND, {open: true});
  };

  if (isPlanningDocPublished) {
    return employee ? (
      <EmployeeChip employee={employee} />
    ) : (
      <Text size="small" color="subtle">
        {t('workstream_no_dri')}
      </Text>
    );
  }

  return (
    <Box ref={ref}>
      <EmployeeSelect
        selection={employee ?? undefined}
        setSelection={handleUpdate}
        disabled={isLoadingMetric}
        chipContentMaxWidth={cellWidth - 75}
        inputClassName={styles.workstreamDriInput}
      />
    </Box>
  );
};

const WorkstreamMetricEta = ({
  editor,
  metric,
  handleMetricChange,
  isLoadingMetric,
}: {
  editor: LexicalEditor;
  metric: WorkstreamMetric;
  handleMetricChange: (
    metricid: number,
    metricUpdate: Partial<WorkstreamMetric>,
    reason: string
  ) => Promise<void>;
  isLoadingMetric: boolean;
}) => {
  const planningId = usePlanningId();
  const isPlanningDocPublished = useIsPlanningDocPublished(planningId ?? '');

  const [endDate, setEndDate] = useState<string | null>(metric?.complete_date ?? null);

  // Update end date when metric complete date changes
  useEffect(() => {
    setEndDate(metric?.complete_date ?? null);
  }, [metric?.complete_date]);

  useEffect(() => {
    if (!endDate || endDate === metric.complete_date) return;
    handleMetricChange(metric.id, {complete_date: endDate}, 'Update metric ETA');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [metric.id, endDate]);

  return (
    <DatePickerEditor
      disabled={!editor.isEditable() || isPlanningDocPublished}
      date={endDate}
      setDate={setEndDate}
      placeholder={isLoadingMetric ? '' : t('no_eta')}
      disableNoDateSelection
    />
  );
};

export const AddWorkstreamComponent = ({
  editor,
  onSelect,
  showNoText,
  archiveWorkstreams,
  driOverride,
  disableCreateWorkstream,
  buttonLabelKey = 'add_workstream',
  selectedWorkstreamIds = [],
  blockedWorkstreamIds = [],
}: {
  editor: LexicalEditor;
  onSelect: (workstream: WorkstreamBasic) => Promise<void>;
  archiveWorkstreams: boolean;
  showNoText?: boolean;
  driOverride?: EmployeeRoleLevel;
  disableCreateWorkstream?: boolean;
  /** i18n key for the closed-state button label (default: add_workstream). */
  buttonLabelKey?: string;
  selectedWorkstreamIds?: number[];
  blockedWorkstreamIds?: number[];
}) => {
  const [isFetching, setIsFetching] = useState(false);
  const typeaheadRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const setSnackbarMessage = useSetAtom(snackbarAtom);
  const typeaheadSelectedIds = useMemo(
    () => [...new Set([...blockedWorkstreamIds, ...selectedWorkstreamIds])],
    [blockedWorkstreamIds, selectedWorkstreamIds]
  );

  useOutsideClick(typeaheadRef, () => setIsOpen(false));

  const handleOpen = () => {
    setIsOpen(true);
  };

  if (isOpen) {
    return (
      <Box ref={typeaheadRef} marginTop={showNoText ? '4' : '0'}>
        <WorkstreamTypeahead
          placeholder={t('find_or_create_workstream')}
          selectedIds={typeaheadSelectedIds}
          menuStyle={{minWidth: 365}}
          onSelect={async (workstream) => {
            const validationErrorKey = getSubworkstreamSelectionErrorKey({
              candidateId: workstream.id,
              blockedWorkstreamIds,
              selectedWorkstreamIds,
            });

            if (validationErrorKey) {
              setSnackbarMessage({text: t(validationErrorKey)});
              return;
            }

            editor.dispatchCommand(TOGGLE_SELECTED_TEXT_TOOLBAR_COMMAND, {open: false});
            setIsFetching(true);
            await onSelect(workstream);
            setIsOpen(false);
            setIsFetching(false);
            editor.dispatchCommand(TOGGLE_SELECTED_TEXT_TOOLBAR_COMMAND, {open: true});
          }}
          inputIcon={
            <UIIcon src={WorkstreamsLine} className={atoms({color: 'Text Base', marginTop: '8'})} />
          }
          archiveWorkstreams={archiveWorkstreams}
          driOverride={driOverride}
          disableCreateWorkstream={disableCreateWorkstream}
          autoFocus
        />
      </Box>
    );
  }
  if (showNoText) {
    return (
      <Box marginTop="4" style={{height: '40px'}}>
        <IconButton
          variant={isFetching ? 'opacity' : 'outline'}
          size="small"
          shape="circular"
          onClick={handleOpen}
          disabled={isFetching}
          aria-label="icon button"
        >
          <UIIcon src={AddLine} size="small" />
        </IconButton>
      </Box>
    );
  }
  return (
    <WorkstreamButtonComponent iconSrc={AddLine} text={t(buttonLabelKey)} onClick={handleOpen} />
  );
};

export const WorkstreamNameWrapper = ({
  editor,
  workstreamId,
  onRemoveWorkstream,
  initialContentEditorState,
  handleContentEditorStateChange,
  hideActionMenu = false,
  hideButtons = false,
  hideDescription = false,
  nameTextSize = 'medium',
}: {
  editor: LexicalEditor;
  workstreamId: number;
  onRemoveWorkstream?: (workstreamId: number) => Promise<void>;
  initialContentEditorState: string | undefined;
  handleContentEditorStateChange: HandleContentEditorStateChange;
  hideActionMenu?: boolean;
  hideButtons?: boolean;
  hideDescription?: boolean;
  nameTextSize?: EditorTextSize;
}) => {
  const planningId = usePlanningId();
  const isPlanningDocPublished = useIsPlanningDocPublished(planningId ?? '');
  const {workstream} = useWorkstream(workstreamId);
  const isWorkstreamRestricted = workstream?.name === 'Restricted';

  if (!workstream) {
    return null;
  }

  const switchboardId = workstream.editor_doc_id;
  const useSwitchboardTemplate =
    workstream.action_type === WorkstreamActionType.SWITCHBOARD ||
    workstream.action_type === WorkstreamActionType.REFRESH;
  const showPublishButton = workstream.archived && !useSwitchboardTemplate;

  let rowLexical: string | undefined;
  let editorStateMapJson: string | undefined;
  if (initialContentEditorState) {
    try {
      const parsed = JSON.parse(initialContentEditorState) as Record<string, unknown>;
      const isLexicalDocShape = Boolean(
        parsed && typeof parsed === 'object' && !Array.isArray(parsed) && 'root' in parsed
      );
      if (isLexicalDocShape) {
        rowLexical = initialContentEditorState;
        editorStateMapJson = undefined;
      } else {
        editorStateMapJson = initialContentEditorState;
        const map = parsed as Record<string, string | undefined>;
        const key = String(workstreamId);
        rowLexical = typeof map[key] === 'string' ? map[key] : undefined;
      }
    } catch {
      rowLexical = initialContentEditorState;
      editorStateMapJson = undefined;
    }
  }

  return (
    <Box
      display="flex"
      flexDirection="column"
      position="relative"
      marginLeft="8"
      className={styles.workstreamNameContainer}
    >
      <WorkstreamName
        editor={editor}
        workstream={workstream}
        editorStateMapJson={editorStateMapJson}
        initialContentEditorState={rowLexical}
        handleContentEditorStateChange={handleContentEditorStateChange}
        onRemoveWorkstream={onRemoveWorkstream}
        nameTextSize={nameTextSize}
      />
      {!hideDescription && workstream.description && (
        <WorkstreamDescription description={workstream.description} />
      )}
      {!isWorkstreamRestricted && !hideButtons && (
        <Box display="flex" alignItems="center" flexWrap="wrap" marginTop="8" style={{gap: '8px'}}>
          {!isPlanningDocPublished && !hideActionMenu && (
            <WorkstreamActionMenu workstream={workstream} />
          )}
          <WorkstreamButtons
            workstreamId={workstreamId}
            switchboardId={switchboardId ?? undefined}
            showCreateSwitchboard={useSwitchboardTemplate}
            showPublishButton={showPublishButton}
          />
        </Box>
      )}
    </Box>
  );
};

const WorkstreamName = ({
  editor,
  workstream,
  editorStateMapJson,
  initialContentEditorState,
  handleContentEditorStateChange,
  onRemoveWorkstream,
  nameTextSize,
}: {
  editor: LexicalEditor;
  workstream: WorkstreamAccess;
  editorStateMapJson?: string;
  initialContentEditorState: string | undefined;
  handleContentEditorStateChange: HandleContentEditorStateChange;
  onRemoveWorkstream?: (workstreamId: number) => Promise<void>;
  nameTextSize: EditorTextSize;
}) => {
  const planningId = usePlanningId();
  const isWorkstreamRestricted = workstream.name === 'Restricted';

  const [isEditing, setIsEditing] = useState(false);
  const [value, setValue] = useState(workstream.name);
  const setSnackbarMessage = useSetAtom(snackbarAtom);

  useSyncWorkstreamNameWithEditorState({
    workstream,
    initialContentEditorState,
    handleContentEditorStateChange,
  });

  const workstreamRef = useRef(workstream);
  useEffect(() => {
    workstreamRef.current = workstream;
    setValue(workstream.name);
  }, [workstream]);

  const nameRef = useRef<HTMLDivElement>(null);
  useOutsideClick(nameRef, () => {
    setIsEditing(false);
  });

  const {editWorkstream} = useWorkstreamEdit({
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ['planning', 'workstreams', planningId]});
      setIsEditing(false);
      editor.dispatchCommand(TOGGLE_SELECTED_TEXT_TOOLBAR_COMMAND, {open: true});
    },
  });

  const debouncedEditWorkstream = useMemo(() => {
    return debounce(async (newValue: string, reason?: string) => {
      editor.dispatchCommand(TOGGLE_SELECTED_TEXT_TOOLBAR_COMMAND, {open: false});
      const ws = workstreamRef.current;
      try {
        await editWorkstream({
          reason,
          data: {
            ...toWorkstreamEditPayload(ws),
            name: newValue,
          },
        });
      } catch {
        setSnackbarMessage({text: t('error_occurred_please_try_again')});
      }
    }, 300);
  }, [editor, editWorkstream, setSnackbarMessage]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Enter') {
        if (isEditing) {
          setIsEditing(false);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown, true);

    return () => {
      window.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [isEditing]);

  useEffect(() => {
    if (value !== workstreamRef.current.name && !isEditing) {
      debouncedEditWorkstream(value, 'Update workstream name');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, isEditing]);

  const nameContainerHeight = '26px';

  const collabNameValue = editorStateMapJson
    ? resolveStampedEditorStateMapValue(editorStateMapJson, String(workstream.id), workstream.name)
    : (initialContentEditorState ?? value);

  if (isWorkstreamRestricted) {
    return null;
  }

  return (
    <Box ref={nameRef}>
      {isEditing ? (
        <TextArea
          name="workstream-name"
          value={value}
          onChange={(e) => {
            setValue(e.currentTarget.value);
          }}
        />
      ) : (
        <Box
          display="flex"
          alignItems="flex-start"
          justifyContent="space-between"
          style={{gap: '8px'}}
        >
          <Box style={{minHeight: nameContainerHeight}}>
            <PlanningCommentableRichText
              planningId={planningId}
              textBold
              textSize={nameTextSize}
              value={collabNameValue}
              onChange={(state) => {
                handleContentEditorStateChange?.(workstream.id, state.toString(), workstream.name);
              }}
            />
          </Box>
          {!isWorkstreamRestricted && (
            <WorkstreamsOverflowMenu
              editor={editor}
              workstreamId={workstream.id}
              onDelete={onRemoveWorkstream}
              setIsEditing={setIsEditing}
            />
          )}
        </Box>
      )}
    </Box>
  );
};

const WorkstreamDescription = ({description}: {description: string}) => {
  const [descriptionExpanded, setDescriptionExpanded] = useState(false);
  return (
    <Box cursor="pointer" onClick={() => setDescriptionExpanded(!descriptionExpanded)}>
      <RichTextArea
        editable={false}
        autoFocus={false}
        value={description}
        truncateLines={descriptionExpanded ? undefined : 2}
        textColor="subtle"
        textSize="small"
      />
    </Box>
  );
};

export const WorkstreamNameAndDri = ({
  workstream,
  disableDraftWorkstreams,
  editorStateMapJson,
  initialContentEditorState,
  handleContentEditorStateChange,
  onPersistDriContentEditorStateChange,
  onPersistNameAndDriContentEditorStateChange,
  combineNameAndDriComments,
}: {
  workstream: WorkstreamAccess;
  disableDraftWorkstreams?: boolean;
  editorStateMapJson?: string;
  initialContentEditorState: string | undefined;
  handleContentEditorStateChange: HandleContentEditorStateChange;
  /**
   * When set with `editorStateMapJson`, enables collab comments on the DRI name (same map as title).
   * Map key: {@link planningMapKeys.workstreamTable.dri}.
   */
  onPersistDriContentEditorStateChange?: (
    serializedState: string | undefined,
    sourceText: string | undefined
  ) => void;
  onPersistNameAndDriContentEditorStateChange?: (
    serializedState: string | undefined,
    sourceText: string | undefined
  ) => void;
  combineNameAndDriComments?: boolean;
}) => {
  const owner = getWorkstreamDri(workstream);

  const planningId = usePlanningId();
  useSyncWorkstreamNameWithEditorState({
    workstream,
    initialContentEditorState,
    handleContentEditorStateChange,
  });

  const isDraftWorkstream = workstream.archived && workstream.status === WorkstreamStatus.DRAFT;
  const isDeletedWorkstream = workstream.archived && workstream.status !== WorkstreamStatus.DRAFT;
  const shouldDisableText = (disableDraftWorkstreams && isDraftWorkstream) || isDeletedWorkstream;
  const disabledText = isDraftWorkstream
    ? t('workstream_draft_banner_title')
    : workstream.archived
      ? t('workstream_archived_banner_title')
      : undefined;
  const disabledClassName = shouldDisableText ? styles.workstreamDisabledText : undefined;
  const labelValue = editorStateMapJson
    ? resolveStampedEditorStateMapValue(editorStateMapJson, String(workstream.id), workstream.name)
    : (initialContentEditorState ?? workstream.name);
  const driMapKey = planningMapKeys.workstreamTable.dri(workstream.id);
  const driValue =
    owner?.name && editorStateMapJson
      ? resolveStampedEditorStateMapValue(editorStateMapJson, driMapKey, owner.name)
      : owner?.name;
  const combinedSecondaryText = shouldDisableText ? disabledText?.toString() : owner?.name;
  const nameAndDriMapKey = planningMapKeys.workstreamTable.nameAndDri(workstream.id);
  const nameAndDriLines = [
    [structuredCommentSegment('name', workstream.name)],
    ...(combinedSecondaryText
      ? [[structuredCommentSegment('secondary', combinedSecondaryText)]]
      : []),
  ];

  const driSubText =
    shouldDisableText && disabledText ? (
      disabledText
    ) : owner?.name && editorStateMapJson && onPersistDriContentEditorStateChange && driValue ? (
      <Suspense
        fallback={
          <Text color="subtle" size="small">
            {owner.name}
          </Text>
        }
      >
        <PlanningCommentableRichText
          planningId={planningId}
          value={driValue}
          editable={false}
          hideToolbar
          disableBorder
          minHeight={0}
          editablePaddingX="0"
          editablePaddingY="0"
          squareBorders
          textColor="subtle"
          textSize="small"
          selectionControls={['comments']}
          onChange={(state) => {
            onPersistDriContentEditorStateChange(state.toString(), owner.name);
          }}
        />
      </Suspense>
    ) : (
      owner?.name
    );

  if (
    combineNameAndDriComments &&
    editorStateMapJson &&
    onPersistNameAndDriContentEditorStateChange
  ) {
    return (
      <LabelGroup
        withStartAccessory={<UIIcon src={WorkstreamsLine} className={disabledClassName} />}
        withLabel={
          <Box className={disabledClassName}>
            <PlanningStructuredCommentField
              planningId={planningId}
              mapKey={nameAndDriMapKey}
              mapJson={editorStateMapJson}
              lines={nameAndDriLines}
              textSize="medium"
              contentClassName={cx(
                styles.workstreamTypeaheadTitleContent,
                styles.workstreamNameAndDriContent
              )}
              selectionControls={['comments']}
              onPersistKey={(_key, serializedState, sourceText) => {
                onPersistNameAndDriContentEditorStateChange(serializedState, sourceText);
              }}
            />
          </Box>
        }
        accessoryGap="4"
        labelGap="0"
      />
    );
  }

  return (
    <LabelGroup
      withStartAccessory={<UIIcon src={WorkstreamsLine} className={disabledClassName} />}
      withLabel={
        <Box className={disabledClassName}>
          <PlanningCommentableRichText
            planningId={planningId}
            value={labelValue}
            contentClassName={styles.workstreamTypeaheadTitleContent}
            onChange={(state) => {
              handleContentEditorStateChange?.(workstream.id, state.toString(), workstream.name);
            }}
          />
        </Box>
      }
      withSubText={
        <Text color="subtle" size="small" className={disabledClassName}>
          {driSubText}
        </Text>
      }
      accessoryGap="4"
      labelGap="0"
    />
  );
};

const WORKSTREAM_ROLE_SELECT_CONFIG: Record<
  string,
  {
    getEmployee: (w: WorkstreamAccess) => EmployeeRoleLevel | undefined;
    noEmployeeKey: string;
    reason: string;
  }
> = {
  dri: {
    getEmployee: getWorkstreamDri,
    noEmployeeKey: 'workstream_no_dri',
    reason: 'Update DRI',
  },
  exec_sponsor: {
    getEmployee: getWorkstreamExecSponsor,
    noEmployeeKey: '—',
    reason: 'Update exec sponsor',
  },
};

const buildWorkstreamEditPayload = (
  workstream: WorkstreamAccess,
  employee_associations: {dri: string; user_id: string}[]
): WorkstreamEdit =>
  ({
    ...workstream,
    metrics: getFullWorkstreamMetrics(workstream.metrics).map((metric) => ({
      ...metric,
      owner_id: metric.owner?.user_id ?? null,
      url: metric.url ?? null,
      is_mnpi: metric.is_mnpi ?? false,
    })),
    dependencies: workstream.dependencies.map((dependency) => ({
      ...dependency,
      dependent_id: dependency.id,
    })),
    employee_associations,
    teams: workstream.teams?.map(({team_id}) => team_id),
    private_employees: workstream.private_employees?.map(({user_id}) => user_id),
    private_teams: workstream.private_teams?.map(({team_id}) => team_id),
  }) as WorkstreamEdit;

const replaceRoleAssociations = (
  workstream: WorkstreamAccess,
  roleId: string,
  employees: EmployeeRoleLevel[]
) => {
  const existing = (workstream.employee_associations ?? []).map((association) => ({
    dri: association.dri,
    user_id: association.employee.user_id,
  }));
  const withoutRole = existing.filter((association) => association.dri !== roleId);
  const seen = new Set<string>();
  const roleAssociations = employees.flatMap((employee) => {
    if (seen.has(employee.user_id)) return [];
    seen.add(employee.user_id);
    return [{dri: roleId, user_id: employee.user_id}];
  });

  return [...withoutRole, ...roleAssociations];
};

const WorkstreamRoleSelect = ({
  editor,
  workstream,
  roleId,
  onSelect,
  showFullEmployeeName,
  containerClassName,
  inputClassName,
}: {
  editor: LexicalEditor;
  workstream: WorkstreamAccess;
  roleId: 'dri' | 'exec_sponsor';
  onSelect?: (workstream: WorkstreamBasic, sync: boolean) => void;
  /** When true, DRI chip shows full name (no table-cell width cap). Use in embeds / narrow parents. */
  showFullEmployeeName?: boolean;
  containerClassName?: string;
  inputClassName?: string;
}) => {
  const config = WORKSTREAM_ROLE_SELECT_CONFIG[roleId];
  const planningId = usePlanningId();
  const isPlanningDocPublished = useIsPlanningDocPublished(planningId ?? '');
  const isWorkstreamRestricted = workstream.name === 'Restricted';
  const employee = config.getEmployee(workstream);

  const ref = useRef<HTMLDivElement>(null);
  const {cellWidth} = useTableCellWidth({
    ref,
    trigger: workstream.id,
    enabled: !showFullEmployeeName,
  });

  const setSnackbarMessage = useSetAtom(snackbarAtom);

  const {editWorkstream} = useWorkstreamEdit({
    onSuccess: (newWorkstream) => {
      onSelect?.(newWorkstream as WorkstreamBasic, false);
    },
  });

  const handleUpdate = async (newEmployee?: Employee) => {
    if (!workstream) {
      return;
    }

    editor.dispatchCommand(TOGGLE_SELECTED_TEXT_TOOLBAR_COMMAND, {open: false});

    const employee_associations = replaceRoleAssociations(
      workstream,
      roleId,
      newEmployee ? [newEmployee] : []
    );

    try {
      await editWorkstream({
        reason: config.reason,
        data: buildWorkstreamEditPayload(workstream, employee_associations),
        optimisticEmployeesByRole: {
          [roleId]: newEmployee ? (newEmployee as EmployeeRoleLevel) : null,
        },
      });
    } catch {
      setSnackbarMessage({text: t('error_occurred_please_try_again')});
    } finally {
      editor.dispatchCommand(TOGGLE_SELECTED_TEXT_TOOLBAR_COMMAND, {open: true});
    }
  };

  if (!workstream) return null;

  if (isPlanningDocPublished || isWorkstreamRestricted) {
    const content = employee ? (
      <EmployeeChip employee={employee} />
    ) : (
      <Text size="small" color="subtle">
        {t(config.noEmployeeKey)}
      </Text>
    );

    return containerClassName ? <Box className={containerClassName}>{content}</Box> : content;
  }

  const chipContentMaxWidth = showFullEmployeeName ? undefined : cellWidth - 75;

  return (
    <Box ref={ref} className={containerClassName}>
      <EmployeeSelect
        selection={employee}
        setSelection={handleUpdate}
        chipContentMaxWidth={chipContentMaxWidth}
        inputClassName={cx(styles.workstreamDriInput, inputClassName)}
      />
    </Box>
  );
};

export const WorkstreamDriWrapper = ({
  editor,
  workstreamId,
  showFullEmployeeName,
  containerClassName,
  inputClassName,
}: {
  editor: LexicalEditor;
  workstreamId: number;
  showFullEmployeeName?: boolean;
  containerClassName?: string;
  inputClassName?: string;
}) => {
  const {workstream} = useWorkstream(workstreamId);
  if (!workstream) return null;
  return (
    <WorkstreamRoleSelect
      editor={editor}
      workstream={workstream}
      roleId="dri"
      showFullEmployeeName={showFullEmployeeName}
      containerClassName={containerClassName}
      inputClassName={inputClassName}
    />
  );
};

export const WorkstreamDri = ({
  editor,
  workstream,
  onSelect,
}: {
  editor: LexicalEditor;
  workstream: WorkstreamAccess;
  onSelect?: (workstream: WorkstreamBasic, sync: boolean) => void;
}) => (
  <WorkstreamRoleSelect editor={editor} workstream={workstream} roleId="dri" onSelect={onSelect} />
);

export const WorkstreamExecSponsorWrapper = ({
  editor,
  workstreamId,
}: {
  editor: LexicalEditor;
  workstreamId: number;
}) => {
  const {workstream} = useWorkstream(workstreamId);
  if (!workstream) return null;
  return <WorkstreamRoleSelect editor={editor} workstream={workstream} roleId="exec_sponsor" />;
};

export const WorkstreamExecSponsor = ({
  editor,
  workstream,
}: {
  editor: LexicalEditor;
  workstream: WorkstreamAccess;
}) => <WorkstreamRoleSelect editor={editor} workstream={workstream} roleId="exec_sponsor" />;

const WorkstreamWorkingGroup = ({
  workstream,
}: {
  editor: LexicalEditor;
  workstream: WorkstreamAccess;
}) => {
  const planningId = usePlanningId();
  const isPlanningDocPublished = useIsPlanningDocPublished(planningId ?? '');
  const isWorkstreamRestricted = workstream.name === 'Restricted';
  const [employees, setEmployees] = useState<EmployeeRoleLevel[]>(() =>
    getWorkstreamWorkingGroup(workstream)
  );

  useEffect(() => {
    setEmployees(getWorkstreamWorkingGroup(workstream));
  }, [workstream]);

  const {editWorkstream} = useWorkstreamEdit();

  const handleUpdate = async (newEmployees: Employee[]) => {
    if (!workstream) return;

    const dedupedEmployees = Array.from(
      new Map(newEmployees.map((employee) => [employee.user_id, employee])).values()
    );

    setEmployees(dedupedEmployees);

    await editWorkstream({
      reason: 'Update working team',
      data: buildWorkstreamEditPayload(
        workstream,
        replaceRoleAssociations(workstream, 'working_group', dedupedEmployees)
      ),
    });
  };

  if (isPlanningDocPublished || isWorkstreamRestricted) {
    return employees.length > 0 ? (
      <Box display="flex" flexWrap="wrap" style={{gap: '8px'}}>
        {employees.map((employee) => (
          <EmployeeChip key={employee.user_id} employee={employee} />
        ))}
      </Box>
    ) : (
      <Text size="small" color="subtle">
        {'\u2014'}
      </Text>
    );
  }

  return (
    <MultiEmployeeSelect
      selections={employees}
      setSelections={handleUpdate}
      autoFocus={false}
      inputClassName={styles.workstreamDriInput}
    />
  );
};

export const WorkstreamWorkingGroupWrapper = ({
  editor,
  workstreamId,
}: {
  editor: LexicalEditor;
  workstreamId: number;
}) => {
  const {workstream} = useWorkstream(workstreamId);
  if (!workstream) return null;
  return <WorkstreamWorkingGroup editor={editor} workstream={workstream} />;
};

const WorkstreamObjectiveWrapper = ({
  workstreamId,
  initialContentEditorState,
  handleContentEditorStateChange,
}: {
  workstreamId: number;
  initialContentEditorState: string | undefined;
  handleContentEditorStateChange: HandleContentEditorStateChange;
}) => {
  const {workstream} = useWorkstream(workstreamId);
  if (!workstream) return null;
  return (
    <WorkstreamObjective
      workstream={workstream}
      initialContentEditorState={initialContentEditorState}
      handleContentEditorStateChange={handleContentEditorStateChange}
    />
  );
};

const WorkstreamObjective = ({
  workstream,
}: {
  workstream: WorkstreamAccess;
  initialContentEditorState: string | undefined;
  handleContentEditorStateChange: HandleContentEditorStateChange;
}) => {
  const planningId = usePlanningId();

  return (
    <PlanningCommentableRichText
      planningId={planningId}
      editable={false}
      hideToolbar
      disableBorder
      minHeight={0}
      editablePaddingX="0"
      editablePaddingY="0"
      squareBorders
      textColor="subtle"
      textSize="small"
      value={serializeObjectiveRichText(workstream.description)}
    />
  );
};

const serializeObjectiveRichText = (value: string | null | undefined) => {
  if (!value) {
    return '';
  }

  if (isLexicalJsonString(value)) {
    return value;
  }

  return toPlainTextLexicalJson(value);
};

export const WorkstreamObjectiveEditableWrapper = ({
  workstreamId,
  editorStateMapJson,
  onPersistKey,
}: {
  workstreamId: number;
  editorStateMapJson?: string;
  onPersistKey?: (
    entryKey: string,
    serializedState: string | undefined,
    sourceText: string | undefined
  ) => void;
  // Compatibility props are ignored because workstream.description is now the objective source of truth.
  initialContentEditorState?: string | undefined;
  handleContentEditorStateChange?: HandleContentEditorStateChange;
}) => {
  const {workstream} = useWorkstream(workstreamId);
  if (!workstream) return null;
  return (
    <WorkstreamObjectiveEditable
      workstream={workstream}
      editorStateMapJson={editorStateMapJson}
      onPersistKey={onPersistKey}
    />
  );
};

export const WorkstreamObjectiveEditable = ({
  workstream,
  editorStateMapJson,
  onPersistKey,
}: {
  workstream: WorkstreamAccess;
  editorStateMapJson?: string;
  onPersistKey?: (
    entryKey: string,
    serializedState: string | undefined,
    sourceText: string | undefined
  ) => void;
  initialContentEditorState?: string | undefined;
  handleContentEditorStateChange?: HandleContentEditorStateChange;
}) => {
  const planningId = usePlanningId();
  const isPlanningDocPublished = useIsPlanningDocPublished(planningId ?? '');
  const {editDescription} = useDescriptionEditor(workstream.id);
  const serverDescriptionValue = useMemo(
    () => serializeObjectiveRichText(workstream.description),
    [workstream.description]
  );

  // Stable key for persisting editor state with comments
  const stableKey = planningMapKeys.strategyKernel.objective(workstream.id);

  // Get editor state with comments from map, checking staleness against plain text.
  // Returns rich text from either the map (if fresh) or DB (if stale/missing)
  const editorStateValue = resolveStampedEditorStateMapValue(
    editorStateMapJson,
    stableKey,
    serverDescriptionValue
  );

  // Maintain local state for the editor value that updates as user types
  const [localEditorState, setLocalEditorState] = useState(editorStateValue);
  const [isObjectiveFocused, setIsObjectiveFocused] = useState(false);
  const [isSavingObjective, setIsSavingObjective] = useState(false);
  const prevEditorStateValue = useRef(editorStateValue);
  const isEditingObjective = isObjectiveFocused || isSavingObjective;

  // Sync editorStateMap (real-time) and server value to local state
  useEffect(() => {
    // Only update if editorStateValue itself changed (external update)
    if (editorStateValue !== prevEditorStateValue.current) {
      prevEditorStateValue.current = editorStateValue;
      if (isEditingObjective) {
        return;
      }
      setLocalEditorState(editorStateValue);
    }
  }, [editorStateValue, isEditingObjective]);

  const handleBlur = useCallback(async () => {
    const serializedLocalState = serializeObjectiveRichText(localEditorState);
    const previousServerDescription = workstream.description ?? '';
    try {
      if (serializedLocalState === previousServerDescription) {
        return; // Description has not changed
      }

      setIsSavingObjective(true);
      // Save serialized rich text (lexical JSON) to persist formatting
      await editDescription(serializedLocalState);
    } catch {
      // On error, revert to server value
      const serverEditorStateValue = resolveStampedEditorStateMapValue(
        editorStateMapJson,
        stableKey,
        serverDescriptionValue
      );
      setLocalEditorState(serverEditorStateValue);
    } finally {
      setIsSavingObjective(false);
    }
  }, [
    localEditorState,
    editDescription,
    workstream.description,
    editorStateMapJson,
    stableKey,
    serverDescriptionValue,
  ]);

  if (isPlanningDocPublished) {
    return (
      <div className={cx(styles.workstreamObjectiveEditor, styles.workstreamObjectiveDisplay)}>
        {serverDescriptionValue ? (
          <PlanningCommentableRichText
            planningId={planningId}
            editable={false}
            hideToolbar
            disableBorder
            minHeight={0}
            editablePaddingX="0"
            editablePaddingY="0"
            squareBorders
            links={false}
            mentions={false}
            value={localEditorState}
            placeholder={t('add_workstream_objective')}
            contentClassName={styles.workstreamObjectiveRichTextContent}
          />
        ) : (
          <span className={styles.workstreamObjectivePlaceholder}>
            {t('add_workstream_objective')}
          </span>
        )}
      </div>
    );
  }

  return (
    <div className={styles.workstreamObjectiveEditor} onBlur={handleBlur}>
      <PlanningCommentableRichText
        key={`workstream-objective-${workstream.id}`}
        planningId={planningId}
        editable
        hideToolbar
        disableBorder
        minHeight={400}
        editablePaddingX="0"
        editablePaddingY="0"
        squareBorders
        links={false}
        mentions={false}
        disableTables
        selectionControls={['styling', 'highlighting', 'comments']}
        value={localEditorState}
        contentClassName={styles.workstreamObjectiveRichTextContent}
        placeholder={isEditingObjective ? undefined : t('add_workstream_objective')}
        placeholderOffsetX={0}
        onToggleFocused={setIsObjectiveFocused}
        onChange={(state) => {
          const stateString = state.toString();

          // Update local state so the editor reflects changes as user types
          setLocalEditorState(stateString);

          // Extract plain text for staleness checking
          const plainText = parseAndExtractTextFromLexicalJson(stateString).trim();

          // Persist rich editor state with comments to Hocuspocus
          onPersistKey?.(stableKey, stateString, plainText);
        }}
      />
    </div>
  );
};

const WorkstreamOutcomesWrapper = ({
  workstreamId,
  initialContentEditorState,
  handleContentEditorStateChange,
  isLongterm,
}: {
  workstreamId: number;
  initialContentEditorState: string | undefined;
  handleContentEditorStateChange: HandleContentEditorStateChange;
  isLongterm: boolean;
}) => {
  const {workstream} = useWorkstream(workstreamId);
  if (!workstream) {
    return null;
  }
  return (
    <WorkstreamOutcomes
      workstream={workstream}
      initialContentEditorState={initialContentEditorState}
      handleContentEditorStateChange={handleContentEditorStateChange}
      isLongterm={isLongterm}
    />
  );
};

const WorkstreamEtaWrapper = ({
  editor,
  workstreamId,
}: {
  editor: LexicalEditor;
  workstreamId: number;
}) => {
  const {workstream} = useWorkstream(workstreamId);
  if (!workstream) return null;
  return <WorkstreamEta editor={editor} workstream={workstream} />;
};

const WorkstreamEta = ({
  editor,
  workstream,
}: {
  editor: LexicalEditor;
  workstream: WorkstreamAccess;
}) => {
  const planningId = usePlanningId();
  const isPlanningDocPublished = useIsPlanningDocPublished(planningId ?? '');
  const {editWorkstream} = useWorkstreamEdit();

  const [endDate, setEndDate] = useState<string | null>(workstream?.end_date ?? null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (endDate === workstream.end_date) return;

    setIsLoading(true);
    editWorkstream({
      reason: 'Update end date',
      data: {
        ...workstream,
        end_date: endDate,
        metrics: getFullWorkstreamMetrics(workstream.metrics).map((metric) => ({
          ...metric,
          owner_id: metric.owner?.user_id ?? null,
          url: metric.url ?? null,
          is_mnpi: metric.is_mnpi ?? false,
        })),
        dependencies: workstream.dependencies.map((dependency) => ({
          ...dependency,
          dependent_id: dependency.id,
        })),
        employee_associations: workstream.employee_associations.map((assoc) => ({
          dri: assoc.dri,
          user_id: assoc.employee.user_id,
        })),
        teams: workstream.teams?.map(({team_id}) => team_id),
        private_employees: workstream.private_employees?.map(({user_id}) => user_id),
        private_teams: workstream.private_teams?.map(({team_id}) => team_id),
      } as WorkstreamEdit,
    });
    setIsLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workstream.id, endDate]);

  return (
    <DatePickerEditor
      disabled={!editor.isEditable() || isPlanningDocPublished}
      date={endDate}
      setDate={setEndDate}
      placeholder={isLoading ? '' : t('no_eta')}
    />
  );
};

const WorkstreamOutcomes = ({
  workstream,
  initialContentEditorState,
  handleContentEditorStateChange,
  isLongterm,
}: {
  workstream: WorkstreamAccess;
  initialContentEditorState: string | undefined;
  handleContentEditorStateChange: HandleContentEditorStateChange;
  isLongterm: boolean;
}) => {
  const collabId = useCollabId();
  const planningId = usePlanningId();
  const isPlanningDocPublished = useIsPlanningDocPublished(planningId ?? '');
  const isWorkstreamRestricted = workstream.name === 'Restricted';

  const {
    createMetric,
    updateMetric,
    deleteMetric,
    attachMetric,
    isPending: isEditingWorkstream,
  } = useWorkstreamMetricMutation();

  const visibleMetrics = useMemo(() => workstream.metrics ?? [], [workstream.metrics]);

  const metrics = useMemo(() => {
    const fullVisibleMetrics = visibleMetrics.filter(isFullWorkstreamMetric);
    return isLongterm
      ? fullVisibleMetrics.filter((metric) => metric.is_longterm)
      : fullVisibleMetrics.filter((metric) => !metric.is_longterm);
  }, [isLongterm, visibleMetrics]);

  const persistMetricChange = async (
    metricid: number,
    metricUpdate: undefined | Partial<WorkstreamMetric>,
    reason?: string,
    skipAuditLogOrExistingMetric?: boolean | WorkstreamMetric
  ) => {
    if (!metricUpdate) return;
    const skipAuditLog =
      typeof skipAuditLogOrExistingMetric === 'boolean' ? skipAuditLogOrExistingMetric : false;
    const existingMetricOverride =
      typeof skipAuditLogOrExistingMetric === 'object' ? skipAuditLogOrExistingMetric : undefined;
    const order =
      getFullWorkstreamMetrics(workstream.metrics).filter((m) => m.is_longterm === isLongterm)
        .length + 1;
    if (metricid === -1) {
      await createMetric(workstream.id, metricUpdate, order, reason ?? undefined, skipAuditLog);
    } else {
      const existing =
        existingMetricOverride ??
        getFullWorkstreamMetrics(workstream.metrics).find((m) => m.id === metricid);
      if (!existing) return;
      await updateMetric(
        workstream.id,
        metricid,
        existing,
        metricUpdate,
        reason ?? undefined,
        skipAuditLog
      );
    }
  };

  const handleDeleteMetric = async (metricToDelete: WorkstreamMetric, reason?: string) => {
    await deleteMetric(
      {workstreamId: workstream.id, metricId: metricToDelete.id!},
      reason ?? undefined
    );
  };

  const handleAttachMetric = async (
    attachRequest: {metric_id: number; order?: number | null; is_primary?: boolean},
    reason?: string
  ) => attachMetric(workstream.id, attachRequest, reason ?? undefined);

  const redesignMetrics = metrics.filter((m) => m.type === 'metric');
  const redesignOutcomeMetrics = metrics.filter((m) => m.type !== 'metric');
  const allEditableMetrics = [...redesignMetrics, ...redesignOutcomeMetrics];
  const outcomes = redesignOutcomeMetrics;
  const planningCollabOutcomeComments =
    collabId != null && collabId !== ''
      ? {
          documentId: collabId,
          initialContentEditorState,
          handleContentEditorStateChange,
        }
      : undefined;

  return (
    <RedesignOutcomesList
      workstreamId={workstream.id}
      workstreamName={workstream.name}
      outcomes={outcomes}
      allEditableMetrics={allEditableMetrics}
      owner={getWorkstreamDri(workstream)}
      selectedFilters={[...REDESIGN_OUTCOME_FILTERS]}
      handleMetricChange={async (metricId, metricUpdate, reason, skipAuditLogOrExistingMetric) => {
        await persistMetricChange(metricId, metricUpdate, reason, skipAuditLogOrExistingMetric);
      }}
      onAttachMetric={handleAttachMetric}
      onDelete={async (metricToDelete, reason) => {
        await handleDeleteMetric(metricToDelete, reason);
      }}
      isPending={isEditingWorkstream}
      disableCreateMetric={isPlanningDocPublished || isWorkstreamRestricted}
      planningCollabOutcomeComments={planningCollabOutcomeComments}
    />
  );
};

const WorkstreamMetricsWrapper = ({
  workstreamId,
  initialContentEditorState,
  handleContentEditorStateChange,
}: {
  workstreamId: number;
  initialContentEditorState: string | undefined;
  handleContentEditorStateChange: HandleContentEditorStateChange;
}) => {
  const {workstream} = useWorkstream(workstreamId);
  if (!workstream) {
    return null;
  }
  return (
    <WorkstreamMetrics
      workstream={workstream}
      initialContentEditorState={initialContentEditorState}
      handleContentEditorStateChange={handleContentEditorStateChange}
    />
  );
};

export const PrimaryWorkstreamMetricWrapper = ({
  workstreamId,
  metricType,
  initialContentEditorState,
  handleContentEditorStateChange,
  planningCollabExtras,
  typeaheadSectionLabel,
  typeaheadSummaryMode,
  showTypeaheadMetricStatus,
  showTypeaheadSectionLabel,
  showTypeaheadMnpiMetricIcon,
}: {
  workstreamId: number;
  metricType: 'leading' | 'lagging';
  initialContentEditorState: string | undefined;
  handleContentEditorStateChange: HandleContentEditorStateChange;
  planningCollabExtras?: StrategyKernelScopedPlanningCollabExtras;
  typeaheadSectionLabel?: string;
  typeaheadSummaryMode?: RedesignMetricTypeaheadSummaryMode;
  showTypeaheadMetricStatus?: boolean;
  showTypeaheadSectionLabel?: boolean;
  showTypeaheadMnpiMetricIcon?: boolean;
}) => {
  const collabId = useCollabId();
  const {workstream} = useWorkstream(workstreamId);
  const currentMetricsPeriod = getCurrentMetricsPeriod();

  const editableMetrics = useMemo(
    () => (workstream?.metrics ?? []).filter(isFullWorkstreamMetric),
    [workstream?.metrics]
  );

  const primaryMetric = useMemo(
    () => getPrimaryMetricForWorkstreamType(editableMetrics, workstreamId, metricType),
    [editableMetrics, metricType, workstreamId]
  );

  if (!workstream) {
    return null;
  }

  if (!primaryMetric) {
    return <Text color="subtle">-</Text>;
  }

  const planningCollabOutcomeComments =
    collabId != null && collabId !== ''
      ? {
          documentId: collabId,
          initialContentEditorState,
          handleContentEditorStateChange,
          ...(planningCollabExtras
            ? {
                handleMetricOwnerContentEditorStateChange: (
                  metricId: number,
                  state: string | undefined,
                  sourceText?: string
                ) => {
                  planningCollabExtras.persistScopedFullKey(
                    `${planningCollabExtras.scopedField}:${metricId}:owner`,
                    state,
                    sourceText
                  );
                },
                handleOutcomeEtaEditorStateChange: (
                  mapKey: string,
                  state: string | undefined,
                  sourceText?: string
                ) => {
                  planningCollabExtras.persistScopedFullKey(
                    `${planningCollabExtras.scopedField}:${mapKey}`,
                    state,
                    sourceText
                  );
                },
              }
            : {}),
        }
      : undefined;

  return (
    <RedesignMetricsList
      workstreamId={workstream.id}
      workstreamName={workstream.name}
      metrics={[primaryMetric]}
      allEditableMetrics={editableMetrics}
      owner={getWorkstreamDri(workstream)}
      selectedPeriod={currentMetricsPeriod}
      selectedForecastType="target"
      metricPeriodOptions={[currentMetricsPeriod]}
      disableCreateMetric
      displayMode="typeahead-node"
      planningCollabOutcomeComments={planningCollabOutcomeComments}
      typeaheadSectionLabel={typeaheadSectionLabel}
      typeaheadSummaryMode={typeaheadSummaryMode}
      showTypeaheadMetricStatus={showTypeaheadMetricStatus}
      showTypeaheadSectionLabel={showTypeaheadSectionLabel}
      showTypeaheadMnpiMetricIcon={showTypeaheadMnpiMetricIcon}
    />
  );
};

export const StarredMilestonesWrapper = ({
  workstreamId,
  initialContentEditorState,
  handleContentEditorStateChange,
  planningCollabExtras,
}: {
  workstreamId: number;
  initialContentEditorState: string | undefined;
  handleContentEditorStateChange: HandleContentEditorStateChange;
  planningCollabExtras?: StrategyKernelScopedPlanningCollabExtras;
}) => {
  const collabId = useCollabId();
  const {workstream} = useWorkstream(workstreamId);

  const editableMetrics = useMemo(
    () => (workstream?.metrics ?? []).filter(isFullWorkstreamMetric),
    [workstream?.metrics]
  );

  const starredMilestones = useMemo(
    () =>
      editableMetrics
        .filter((metric) => metric.type === 'milestone' && isPrimaryMetric(metric, workstreamId))
        .sort((a, b) => {
          const aTime = a.complete_date ? new Date(a.complete_date).getTime() : Infinity;
          const bTime = b.complete_date ? new Date(b.complete_date).getTime() : Infinity;
          if (aTime !== bTime) {
            return aTime - bTime;
          }
          return a.id - b.id;
        }),
    [editableMetrics, workstreamId]
  );

  if (!workstream) {
    return null;
  }

  if (starredMilestones.length === 0) {
    return <Text color="subtle">-</Text>;
  }

  const planningCollabOutcomeComments =
    collabId != null && collabId !== ''
      ? {
          documentId: collabId,
          initialContentEditorState,
          handleContentEditorStateChange,
          ...(planningCollabExtras
            ? {
                handleMetricOwnerContentEditorStateChange: (
                  metricId: number,
                  state: string | undefined,
                  sourceText?: string
                ) => {
                  planningCollabExtras.persistScopedFullKey(
                    `${planningCollabExtras.scopedField}:${metricId}:owner`,
                    state,
                    sourceText
                  );
                },
                handleOutcomeEtaEditorStateChange: (
                  mapKey: string,
                  state: string | undefined,
                  sourceText?: string
                ) => {
                  planningCollabExtras.persistScopedFullKey(
                    `${planningCollabExtras.scopedField}:${mapKey}`,
                    state,
                    sourceText
                  );
                },
              }
            : {}),
        }
      : undefined;

  return (
    <RedesignOutcomesList
      workstreamId={workstream.id}
      workstreamName={workstream.name}
      outcomes={starredMilestones}
      allEditableMetrics={editableMetrics}
      owner={getWorkstreamDri(workstream)}
      selectedFilters={['milestone']}
      disableCreateMetric
      planningCollabOutcomeComments={planningCollabOutcomeComments}
      displayMode="typeahead-node"
    />
  );
};

const WorkstreamMetrics = ({
  workstream,
  initialContentEditorState,
  handleContentEditorStateChange,
}: {
  workstream: WorkstreamAccess;
  initialContentEditorState: string | undefined;
  handleContentEditorStateChange: HandleContentEditorStateChange;
}) => {
  const collabId = useCollabId();
  const planningId = usePlanningId();
  const isPlanningDocPublished = useIsPlanningDocPublished(planningId ?? '');
  const isWorkstreamRestricted = workstream.name === 'Restricted';
  const pulseUser = useAtomValue(pulseUserAtom);

  const {
    createMetric,
    updateMetric,
    deleteMetric,
    attachMetric,
    isPending: isEditingWorkstream,
  } = useWorkstreamMetricMutation();

  const visibleMetrics = useMemo(() => workstream.metrics ?? [], [workstream.metrics]);

  const redesignMetrics = useMemo(
    () => visibleMetrics.filter((metric) => metric.type === 'metric'),
    [visibleMetrics]
  );
  const currentMetricsPeriod = getCurrentMetricsPeriod();
  const [selectedMetricsPeriod, setSelectedMetricsPeriod] = useState(currentMetricsPeriod);
  const [selectedMetricsForecastType, setSelectedMetricsForecastType] =
    useState<MetricsForecastType>('target');

  const metricPeriodOptions = useMemo(() => {
    const forecastPeriods = redesignMetrics
      .filter(isFullWorkstreamMetric)
      .flatMap((metric) => metric.metric_forecasts?.map((forecast) => forecast.period) ?? []);

    if (forecastPeriods.length === 0) {
      return [currentMetricsPeriod];
    }

    const sortedForecastPeriods = [...forecastPeriods].sort(compareMetricsPeriods);
    const earliestForecastPeriod = sortedForecastPeriods[0];
    const latestForecastPeriod = sortedForecastPeriods[sortedForecastPeriods.length - 1];
    const minimumLatestPeriod = getMetricsPeriodWithOffset(currentMetricsPeriod, 2);
    const endPeriod =
      compareMetricsPeriods(latestForecastPeriod, minimumLatestPeriod) >= 0
        ? latestForecastPeriod
        : minimumLatestPeriod;
    const startPeriod =
      compareMetricsPeriods(currentMetricsPeriod, earliestForecastPeriod) <= 0
        ? currentMetricsPeriod
        : earliestForecastPeriod;

    return normalizeMetricsPeriodOptions(buildMetricsPeriodRange(startPeriod, endPeriod));
  }, [currentMetricsPeriod, redesignMetrics]);

  useEffect(() => {
    if (metricPeriodOptions.includes(selectedMetricsPeriod)) {
      return;
    }

    setSelectedMetricsPeriod(
      metricPeriodOptions.includes(currentMetricsPeriod)
        ? currentMetricsPeriod
        : (metricPeriodOptions[0] ?? currentMetricsPeriod)
    );
  }, [currentMetricsPeriod, metricPeriodOptions, selectedMetricsPeriod]);

  const persistMetricChange = async (
    metricid: number,
    metricUpdate: undefined | Partial<WorkstreamMetric>,
    reason?: string,
    skipAuditLogOrExistingMetric?: boolean | WorkstreamMetric
  ) => {
    if (!metricUpdate) return;
    const skipAuditLog =
      typeof skipAuditLogOrExistingMetric === 'boolean' ? skipAuditLogOrExistingMetric : false;
    const existingMetricOverride =
      typeof skipAuditLogOrExistingMetric === 'object' ? skipAuditLogOrExistingMetric : undefined;
    const metricOrder =
      getFullWorkstreamMetrics(workstream.metrics).filter((m) => m.type === 'metric').length + 1;
    if (metricid === -1) {
      await createMetric(
        workstream.id,
        metricUpdate,
        metricOrder,
        reason ?? undefined,
        skipAuditLog
      );
    } else {
      const existing =
        existingMetricOverride ??
        getFullWorkstreamMetrics(workstream.metrics).find((m) => m.id === metricid);
      if (!existing) return;
      await updateMetric(
        workstream.id,
        metricid,
        existing,
        metricUpdate,
        reason ?? undefined,
        skipAuditLog
      );
    }
  };

  const handleDeleteMetric = async (metricToDelete: WorkstreamMetric, reason?: string) => {
    await deleteMetric(
      {workstreamId: workstream.id, metricId: metricToDelete.id!},
      reason ?? undefined
    );
  };

  const handleAttachMetric = async (
    attachRequest: {metric_id: number; order?: number | null; is_primary?: boolean},
    reason?: string
  ) => attachMetric(workstream.id, attachRequest, reason ?? undefined);

  const disableModifyMetrics = isWorkstreamStatusArchived(workstream) && !isUserAdmin(pulseUser);

  const planningCollabOutcomeComments =
    collabId != null && collabId !== ''
      ? {
          documentId: collabId,
          initialContentEditorState,
          handleContentEditorStateChange,
        }
      : undefined;

  return (
    <Stack gap="6">
      <Box display="flex" justifyContent="flex-end">
        <RedesignMetricsPeriodSelector
          periods={metricPeriodOptions}
          selectedPeriod={selectedMetricsPeriod}
          selectedForecastType={selectedMetricsForecastType}
          onSelect={setSelectedMetricsPeriod}
          onForecastTypeSelect={setSelectedMetricsForecastType}
        />
      </Box>
      <RedesignMetricsList
        workstreamId={workstream.id}
        workstreamName={workstream.name}
        metrics={redesignMetrics}
        allEditableMetrics={visibleMetrics.filter(isFullWorkstreamMetric)}
        owner={getWorkstreamDri(workstream)}
        selectedPeriod={selectedMetricsPeriod}
        selectedForecastType={selectedMetricsForecastType}
        metricPeriodOptions={metricPeriodOptions}
        handleMetricChange={async (
          metricId,
          metricUpdate,
          reason,
          skipAuditLogOrExistingMetric
        ) => {
          await persistMetricChange(metricId, metricUpdate, reason, skipAuditLogOrExistingMetric);
        }}
        onAttachMetric={handleAttachMetric}
        onDelete={async (metricToDelete, reason) => {
          await handleDeleteMetric(metricToDelete, reason);
        }}
        isPending={isEditingWorkstream}
        disableCreateMetric={
          isPlanningDocPublished || isWorkstreamRestricted || disableModifyMetrics
        }
        planningCollabOutcomeComments={planningCollabOutcomeComments}
      />
    </Stack>
  );
};

const WorkstreamLink = ({workstreamId}: {workstreamId: number}) => {
  const url = `${window.location.origin}${ROUTE_PATHS.WORKSTREAMS_DETAIL.replace(':id', workstreamId.toString())}`;
  return <a href={url}>{url}</a>;
};

export const SubworkstreamSelectionWrapper = ({
  editor,
  workstreamId,
  initialContentEditorState,
  handleContentEditorStateChange,
  onPersistSubworkstreamDri,
  onPersistSubworkstreamNameAndDri,
  hideAddSubworkstream,
  combineNameAndDriComments,
}: {
  editor: LexicalEditor;
  workstreamId: number;
  initialContentEditorState: string | undefined;
  handleContentEditorStateChange: HandleContentEditorStateChange;
  onPersistSubworkstreamDri?: (
    subWorkstreamId: number,
    serializedState: string | undefined,
    sourceText: string | undefined
  ) => void;
  onPersistSubworkstreamNameAndDri?: (
    subWorkstreamId: number,
    serializedState: string | undefined,
    sourceText: string | undefined
  ) => void;
  hideAddSubworkstream?: boolean;
  combineNameAndDriComments?: boolean;
}) => {
  const planningId = usePlanningId();
  const isPlanningDocPublished = useIsPlanningDocPublished(planningId ?? '');
  const {workstream, isLoading} = useWorkstream(workstreamId);
  const isWorkstreamRestricted = workstream?.name === 'Restricted';
  const archiveWorkstreams = !!workstream?.archived;
  const driOverride = getWorkstreamDri(workstream);
  const selectedSubworkstreamIds = workstream?.dependencies.map(
    (dependency) => dependency.dependent.id
  );

  const {editWorkstream} = useWorkstreamEdit();

  if (!workstream || isLoading) return null;

  const handleAddSubworkstream = async (subworkstream: WorkstreamBasic) => {
    const dependencyIds = workstream.dependencies.map((dependency) => dependency.dependent.id);
    if (subworkstream.id === workstream.id || dependencyIds.includes(subworkstream.id)) {
      return;
    }

    const dependencies = [...dependencyIds, subworkstream.id];

    await editWorkstream({
      reason: 'Add subworkstream',
      data: {
        ...workstream,
        metrics: getFullWorkstreamMetrics(workstream.metrics).map((metric) => ({
          ...metric,
          owner_id: metric.owner?.user_id ?? null,
          url: metric.url ?? null,
          is_mnpi: metric.is_mnpi ?? false,
        })),
        dependencies: dependencies
          .filter((dependentId: any) => dependentId > 0)
          .map((dependentId, index) => ({
            dependent_id: dependentId,
            type: WorkstreamDependencyType.DEPENDS_ON,
            order: index + 1,
          })),
        employee_associations: workstream.employee_associations.map((assoc) => ({
          dri: assoc.dri,
          user_id: assoc.employee.user_id,
        })),
        teams: workstream.teams?.map(({team_id}) => team_id),
        private_employees: workstream.private_employees?.map(({user_id}) => user_id),
        private_teams: workstream.private_teams?.map(({team_id}) => team_id),
      } as WorkstreamEdit,
    });
  };

  const subWorkstreams = workstream.dependencies
    .sort((a, b) => a.dependent.name.localeCompare(b.dependent.name))
    .filter((dependency) => {
      const latestUpdate = dependency.dependent.updates?.[0]?.status;
      if (latestUpdate && isWorkstreamStatusClosed(latestUpdate)) {
        return false;
      }
      return true;
    })
    .map((dependency) => dependency.dependent.id);
  if (hideAddSubworkstream && (isWorkstreamRestricted || subWorkstreams.length === 0)) {
    return <Text color="subtle">-</Text>;
  }

  if (isWorkstreamRestricted) {
    return null;
  }

  if (!hideAddSubworkstream && subWorkstreams.length === 0 && !isPlanningDocPublished) {
    return (
      <AddWorkstreamComponent
        editor={editor}
        onSelect={handleAddSubworkstream}
        archiveWorkstreams={archiveWorkstreams}
        driOverride={driOverride}
        selectedWorkstreamIds={selectedSubworkstreamIds}
        blockedWorkstreamIds={[workstream.id]}
      />
    );
  }

  return (
    <Box>
      {subWorkstreams.map((subworkstreamId) => (
        <SubworkstreamTypeaheadSelection
          key={`editor-subworkstream__${subworkstreamId}`}
          editor={editor}
          workstreamId={workstreamId}
          subWorkstreamId={subworkstreamId}
          initialContentEditorState={initialContentEditorState}
          handleContentEditorStateChange={handleContentEditorStateChange}
          onPersistSubworkstreamDri={onPersistSubworkstreamDri}
          onPersistSubworkstreamNameAndDri={onPersistSubworkstreamNameAndDri}
          combineNameAndDriComments={combineNameAndDriComments}
        />
      ))}
      {!hideAddSubworkstream && !isPlanningDocPublished && (
        <AddWorkstreamComponent
          editor={editor}
          onSelect={handleAddSubworkstream}
          archiveWorkstreams={archiveWorkstreams}
          driOverride={driOverride}
          showNoText
          selectedWorkstreamIds={selectedSubworkstreamIds}
          blockedWorkstreamIds={[workstream.id]}
        />
      )}
    </Box>
  );
};

export const SubworkstreamTypeaheadSelection = ({
  editor,
  workstreamId,
  subWorkstreamId,
  initialContentEditorState,
  handleContentEditorStateChange,
  onPersistSubworkstreamDri,
  onPersistSubworkstreamNameAndDri,
  combineNameAndDriComments,
}: {
  editor: LexicalEditor;
  workstreamId: number;
  subWorkstreamId: number;
  initialContentEditorState: string | undefined;
  handleContentEditorStateChange: HandleContentEditorStateChange;
  onPersistSubworkstreamDri?: (
    subWorkstreamId: number,
    serializedState: string | undefined,
    sourceText: string | undefined
  ) => void;
  onPersistSubworkstreamNameAndDri?: (
    subWorkstreamId: number,
    serializedState: string | undefined,
    sourceText: string | undefined
  ) => void;
  combineNameAndDriComments?: boolean;
}) => {
  const {workstream} = useWorkstream(workstreamId);
  const {workstream: subWorkstream} = useWorkstream(subWorkstreamId);
  const {editWorkstream} = useWorkstreamEdit();

  if (!workstream || !subWorkstream) return null;

  const handleDeleteSubworkstream = async () => {
    await editWorkstream({
      reason: 'Delete subworkstream',
      data: {
        ...workstream,
        metrics: getFullWorkstreamMetrics(workstream.metrics).map((metric) => ({
          ...metric,
          owner_id: metric.owner?.user_id ?? null,
          url: metric.url ?? null,
          is_mnpi: metric.is_mnpi ?? false,
        })),
        dependencies: workstream.dependencies
          .filter((dependency) => dependency.dependent.id !== subWorkstreamId)
          .map((dependency) => ({
            ...dependency,
            dependent_id: dependency.id,
          })),
        employee_associations: workstream.employee_associations.map((assoc) => ({
          dri: assoc.dri,
          user_id: assoc.employee.user_id,
        })),
        teams: workstream.teams?.map(({team_id}) => team_id),
        private_employees: workstream.private_employees?.map(({user_id}) => user_id),
        private_teams: workstream.private_teams?.map(({team_id}) => team_id),
      } as WorkstreamEdit,
    });
  };

  const contentEditorStateObj = JSON.parse(initialContentEditorState || '{}');
  return (
    <WorkstreamTypeaheadSelection
      editor={editor}
      basicWorkstream={subWorkstream}
      onDelete={handleDeleteSubworkstream}
      editorStateMapJson={initialContentEditorState ?? '{}'}
      initialContentEditorState={contentEditorStateObj[subWorkstreamId]}
      handleContentEditorStateChange={handleContentEditorStateChange}
      onPersistDriContentEditorStateChange={
        onPersistSubworkstreamDri
          ? (serializedState, sourceText) =>
              onPersistSubworkstreamDri(subWorkstreamId, serializedState, sourceText)
          : undefined
      }
      onPersistNameAndDriContentEditorStateChange={
        onPersistSubworkstreamNameAndDri
          ? (serializedState, sourceText) =>
              onPersistSubworkstreamNameAndDri(subWorkstreamId, serializedState, sourceText)
          : undefined
      }
      combineNameAndDriComments={combineNameAndDriComments}
      showDeleteSubworkstreamModal
      disableDraftWorkstreams
    />
  );
};

export const WorkstreamTypeaheadSelectionWrapper = ({
  editor,
  workstreamId,
  onDelete,
  editorStateMapJson,
  initialContentEditorState,
  handleContentEditorStateChange,
}: {
  editor: LexicalEditor;
  workstreamId: number;
  onDelete: (workstreamId: number) => Promise<void>;
  editorStateMapJson?: string;
  initialContentEditorState: string | undefined;
  handleContentEditorStateChange: HandleContentEditorStateChange;
}) => {
  const {workstream} = useWorkstream(workstreamId);
  if (!workstream) return null;
  return (
    <WorkstreamTypeaheadSelection
      editor={editor}
      basicWorkstream={workstream}
      onDelete={onDelete}
      editorStateMapJson={editorStateMapJson}
      initialContentEditorState={initialContentEditorState}
      handleContentEditorStateChange={handleContentEditorStateChange}
    />
  );
};

export const WorkstreamsOverflowMenu = ({
  editor,
  workstreamId,
  onDelete,
  setIsEditing,
  showDeleteSubworkstreamModal,
  showOpenLinkButton,
}: {
  editor: LexicalEditor;
  workstreamId: number;
  onDelete?: (workstreamId: number) => Promise<void>;
  setIsEditing?: (isEditing: boolean) => void;
  showDeleteSubworkstreamModal?: boolean;
  showOpenLinkButton?: boolean;
}) => {
  const {workstream} = useWorkstream(workstreamId);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const planningId = usePlanningId();
  const isPlanningDocPublished = useIsPlanningDocPublished(planningId ?? '');

  const [isFetching, setIsFetching] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  if (!workstream) return null;

  const handleDeleteWorkstream =
    isPlanningDocPublished || !onDelete
      ? undefined
      : async () => {
          editor.dispatchCommand(TOGGLE_SELECTED_TEXT_TOOLBAR_COMMAND, {open: false});
          setIsFetching(true);
          await onDelete(workstreamId);
          setIsFetching(false);
          editor.dispatchCommand(TOGGLE_SELECTED_TEXT_TOOLBAR_COMMAND, {open: true});
        };

  const showOpenLink = showOpenLinkButton
    ? true
    : // Show open link if workstream is actually deleted (not draft) in case user wants to undelete it
      workstream?.archived
      ? workstream?.status !== WorkstreamStatus.DRAFT
      : false;

  return (
    <Box>
      <Menu.Wrapper
        shouldPropagateClickOutsideMouseEvents={false}
        className={cx(styles.workstreamsOverflowMenu, {[styles.overflowOpen]: isMenuOpen})}
        onToggle={({isOpen}) => setIsMenuOpen(isOpen)}
      >
        {({getContentProps, getTriggerProps}) => {
          const triggerProps = getTriggerProps();
          return (
            <>
              <IconButton
                {...getTriggerProps()}
                variant="transparent"
                className={atoms({display: 'block'})}
                size="small"
                onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                  e.stopPropagation();
                  triggerProps.onClick?.(e);
                }}
                style={{marginTop: '1px'}}
                aria-label="icon button"
              >
                <UIIcon src={MoreHorizontalLine} className={atoms({color: 'Text Subtle'})} />
              </IconButton>

              <Menu.Content {...getContentProps()} minWidth="200px" placement="bottom-start">
                <Menu.Segment>
                  {showOpenLink && (
                    <Menu.LinkItem
                      href={`${ROUTE_PATHS.WORKSTREAMS_DETAIL.replace(':id', workstreamId.toString())}`}
                      target="_blank"
                    >
                      {t('view_workstream')}
                    </Menu.LinkItem>
                  )}

                  {!isPlanningDocPublished && setIsEditing && (
                    <Menu.ActionItem
                      onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                        e.stopPropagation();
                        setIsEditing(true);
                      }}
                    >
                      {t('rename')}
                    </Menu.ActionItem>
                  )}

                  {handleDeleteWorkstream && (
                    <Menu.ActionItem
                      onClick={() => {
                        if (showDeleteSubworkstreamModal) {
                          setShowDeleteModal(true);
                        } else {
                          handleDeleteWorkstream();
                        }
                      }}
                      disabled={isFetching}
                    >
                      {t('remove')}
                    </Menu.ActionItem>
                  )}
                </Menu.Segment>
              </Menu.Content>
            </>
          );
        }}
      </Menu.Wrapper>

      {showDeleteSubworkstreamModal && handleDeleteWorkstream && (
        <DeleteModal
          open={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          title={t('delete_subworkstream_confirm')}
          subtitle={t('delete_subworkstream_confirm_subtitle')}
          confirmLabel={t('remove')}
          isPending={isFetching}
          setSnackbarMessage={null}
          onDelete={handleDeleteWorkstream}
        />
      )}
    </Box>
  );
};

export const WorkstreamTypeaheadSelection = ({
  editor,
  basicWorkstream,
  onDelete,
  editorStateMapJson,
  initialContentEditorState,
  handleContentEditorStateChange,
  onPersistDriContentEditorStateChange,
  onPersistNameAndDriContentEditorStateChange,
  showDeleteSubworkstreamModal = false,
  disableDraftWorkstreams = false,
  showOpenLinkButton = true,
  combineNameAndDriComments = false,
}: {
  editor: LexicalEditor;
  basicWorkstream: WorkstreamBasic;
  onDelete: (workstreamId: number) => Promise<void>;
  editorStateMapJson?: string;
  initialContentEditorState: string | undefined;
  handleContentEditorStateChange: HandleContentEditorStateChange;
  onPersistDriContentEditorStateChange?: (
    serializedState: string | undefined,
    sourceText: string | undefined
  ) => void;
  onPersistNameAndDriContentEditorStateChange?: (
    serializedState: string | undefined,
    sourceText: string | undefined
  ) => void;
  showDeleteSubworkstreamModal?: boolean;
  disableDraftWorkstreams?: boolean;
  showOpenLinkButton?: boolean;
  combineNameAndDriComments?: boolean;
}) => {
  const {workstream} = useWorkstream(basicWorkstream.id);
  const isWorkstreamRestricted = workstream?.name === 'Restricted';

  if (!workstream) return null;

  return (
    <Box
      {...withShade({
        className: styles.workstreamTypeaheadSelection,
      })}
    >
      <WorkstreamNameAndDri
        workstream={workstream}
        disableDraftWorkstreams={disableDraftWorkstreams}
        editorStateMapJson={editorStateMapJson}
        initialContentEditorState={initialContentEditorState}
        handleContentEditorStateChange={handleContentEditorStateChange}
        onPersistDriContentEditorStateChange={onPersistDriContentEditorStateChange}
        onPersistNameAndDriContentEditorStateChange={onPersistNameAndDriContentEditorStateChange}
        combineNameAndDriComments={combineNameAndDriComments}
      />
      {!isWorkstreamRestricted && (
        <WorkstreamsOverflowMenu
          editor={editor}
          workstreamId={workstream.id}
          onDelete={onDelete}
          showDeleteSubworkstreamModal={showDeleteSubworkstreamModal}
          showOpenLinkButton={showOpenLinkButton}
        />
      )}
    </Box>
  );
};

const WorkstreamButtons = ({
  workstreamId,
  switchboardId,
  showCreateSwitchboard,
  showPublishButton,
}: {
  workstreamId: number;
  switchboardId?: string;
  showCreateSwitchboard?: boolean;
  showPublishButton?: boolean;
}) => {
  const planningId = usePlanningId();
  const isPlanningDocPublished = useIsPlanningDocPublished(planningId ?? '');
  const {workstream} = useWorkstream(workstreamId);
  const switchboardActionState = getSwitchboardActionState({
    editorDocId: switchboardId,
  });

  if (!workstream) return null;

  const latestStatus = workstream.updates?.[0]?.status;
  const isClosedWorkstream = latestStatus ? isClosedStatus(latestStatus) : false;

  return (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="flex-end"
      flexWrap="wrap"
      style={{gap: '8px'}}
    >
      {showCreateSwitchboard &&
        (!switchboardActionState.hasSwitchboard ? (
          !isPlanningDocPublished && switchboardActionState.showCreateSwitchboard ? (
            <CreateSwitchboardButton workstreamId={workstreamId} disabled={isClosedWorkstream} />
          ) : null
        ) : (
          <>
            <ViewSwitchboardButton switchboardId={switchboardId!} />
          </>
        ))}
      {showPublishButton && !isPlanningDocPublished && (
        <PublishWorkstreamButton workstream={workstream} />
      )}
    </Box>
  );
};

const CreateSwitchboardButton = ({
  workstreamId,
  disabled,
}: {
  workstreamId: number;
  disabled?: boolean;
}) => {
  const [isFetching, setIsFetching] = useState(false);
  const {mutateAsync: createSwitchboard} = useCreateSwitchboard(workstreamId);
  const handleCreateSwitchboard = async () => {
    if (disabled) return;
    setIsFetching(true);
    await createSwitchboard();
    setIsFetching(false);
  };

  const isButtonDisabled = disabled || isFetching;

  return (
    <WorkstreamButtonComponent
      iconSrc={PaperDocLine}
      text={t('create_switchboard')}
      onClick={handleCreateSwitchboard}
      disabled={isButtonDisabled}
      tooltipTitle={t('workstream_is_closed_tooltip')}
    />
  );
};

const PublishWorkstreamButton = ({workstream}: {workstream: WorkstreamAccess}) => {
  const [isFetching, setIsFetching] = useState(false);

  const {undeleteWorkstream} = useWorkstreamUndelete({
    onSuccess: () => {
      setIsFetching(false);
    },
  });

  const handlePublishWorkstream = async () => {
    setIsFetching(true);
    await undeleteWorkstream(workstream.id);
  };

  return (
    <WorkstreamButtonComponent
      iconSrc={SendLine}
      text={t('publish')}
      onClick={handlePublishWorkstream}
      disabled={isFetching}
    />
  );
};

const ViewSwitchboardButton = ({switchboardId}: {switchboardId: string}) => {
  const handleOpenSwitchboard = async () => {
    window.open(`${ROUTE_PATHS.PLANNING_DOC.replace(':id', switchboardId)}`, '_blank');
  };

  return (
    <WorkstreamButtonComponent
      iconSrc={PaperDocLine}
      text={t('view_switchboard')}
      onClick={handleOpenSwitchboard}
    />
  );
};

const WorkstreamButtonComponent = ({
  iconSrc,
  text,
  onClick,
  disabled,
  tooltipTitle,
}: {
  iconSrc: React.ComponentType<React.SVGAttributes<SVGElement>>;
  text: string;
  onClick: () => void;
  disabled?: boolean;
  tooltipTitle?: string;
}) => {
  return (
    <OptionalTooltip enabled={Boolean(disabled && tooltipTitle)} title={tooltipTitle}>
      <span>
        <Chip
          size="small"
          className={atoms({
            backgroundColor: 'Background Base',
            borderColor: 'Border Subtle',
            borderStyle: 'Solid',
            borderWidth: '1',
          })}
          onClick={onClick}
          disabled={disabled}
        >
          <Chip.IconAccessory>
            <UIIcon src={iconSrc} className={atoms({color: 'Border Base'})} />
          </Chip.IconAccessory>
          <Chip.Content>
            <Text color="subtle" size="small">
              {text}
            </Text>
          </Chip.Content>
        </Chip>
      </span>
    </OptionalTooltip>
  );
};

export const WorkstreamActionMenuWrapper = ({workstreamId}: {workstreamId: number}) => {
  const {workstream} = useWorkstream(workstreamId);
  if (!workstream) return null;
  return <WorkstreamActionMenu workstream={workstream} />;
};

const WorkstreamActionMenu = ({workstream}: {workstream: WorkstreamAccess}) => {
  const [selectedActionType, setSelectedActionType] = useState<
    WorkstreamActionType | null | undefined
  >(workstream.action_type);
  const [isFetching, setIsFetching] = useState(false);

  const {editWorkstream} = useWorkstreamEdit({
    invalidateQueries: false,
    onSuccess: () => {
      setIsFetching(false);
    },
  });

  const hasSwitchboard = !!workstream.editor_doc_id;
  const shouldHideActionMenu =
    (workstream.action_type === WorkstreamActionType.SWITCHBOARD ||
      workstream.action_type === WorkstreamActionType.REFRESH) &&
    hasSwitchboard;
  if (shouldHideActionMenu) {
    return null;
  }

  const handleUpdateAction = async (newAction: WorkstreamActionType) => {
    setIsFetching(true);

    await editWorkstream({
      reason: 'Update action',
      data: {
        ...workstream,
        action_type: newAction,
        metrics: getFullWorkstreamMetrics(workstream.metrics).map((metric) => ({
          ...metric,
          owner_id: metric.owner?.user_id ?? null,
          url: metric.url ?? null,
          is_mnpi: metric.is_mnpi ?? false,
        })),
        dependencies: workstream.dependencies.map((dependency) => ({
          ...dependency,
          dependent_id: dependency.id,
        })),
        employee_associations: workstream.employee_associations.map((assoc) => ({
          dri: assoc.dri,
          user_id: assoc.employee.user_id,
        })),
        teams: workstream.teams?.map(({team_id}) => team_id),
        private_employees: workstream.private_employees?.map(({user_id}) => user_id),
        private_teams: workstream.private_teams?.map(({team_id}) => team_id),
      } as WorkstreamEdit,
    });

    setSelectedActionType(newAction);
  };

  return (
    <Menu.Wrapper onSelection={handleUpdateAction} shouldPropagateClickOutsideMouseEvents>
      {({getContentProps, getTriggerProps}) => {
        return (
          <>
            <Chip {...getTriggerProps()} size="small" disabled={isFetching} withDropdownIcon>
              <Chip.Content>
                {`${getWorkstreamActionIcon(selectedActionType)}${getWorkstreamActionText(selectedActionType)}`}
              </Chip.Content>
            </Chip>
            <Menu.Content {...getContentProps()} minWidth="200px">
              <Menu.Segment>
                {[
                  WorkstreamActionType.NONE,
                  WorkstreamActionType.SWITCHBOARD,
                  WorkstreamActionType.REFRESH,
                ].map((actionType) => (
                  <Menu.ActionItem
                    key={`editor-workstream-action__${actionType}`}
                    value={actionType}
                    withRightAccessory={
                      selectedActionType === actionType && <UIIcon src={CheckmarkLine} />
                    }
                  >
                    {`${getWorkstreamActionIcon(actionType)}${getWorkstreamActionText(actionType)}`}
                  </Menu.ActionItem>
                ))}
              </Menu.Segment>
            </Menu.Content>
          </>
        );
      }}
    </Menu.Wrapper>
  );
};
